import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  adminResetPassword,
  changePassword,
  createPartner,
  createFundUpdate,
  deletePartner,
  deleteDocument,
  deleteFundUpdate,
  deleteResetRequest,
  downloadBackup,
  downloadDocument,
  exportMyResultsCsv,
  exportMyResultsExcel,
  exportPartnersCsv,
  enableTwoFactor,
  getAuditLogs,
  getAdminStatus,
  getDocuments,
  getFundUpdates,
  getImportArchive,
  getImportPeriods,
  getInterimValuation,
  previewInterimValuation,
  applyInterimValuation,
  revertInterimValuation,
  getMe,
  getAllUsers,
  impersonateUser,
  previewExcel,
  setupTwoFactor,
  updateEmergencyContact,
  updateFundUpdate,
  uploadExcel,
  uploadDocument
} from '../api';
import './DashboardPage.css';

const formatCzk = (value = 0) =>
  new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0
  }).format(value || 0);

const formatUsd = (value = 0) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value || 0);

const formatCompactCzk = (value = 0) => {
  const amount = Number(value || 0);

  if (Math.abs(amount) >= 1000000) {
    return `${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)} mil. Kč`;
  }

  if (Math.abs(amount) >= 1000) {
    return `${Math.round(amount / 1000)} tis. Kč`;
  }

  return formatCzk(amount);
};

const formatCompactUsd = (value = 0) => {
  const amount = Number(value || 0);

  if (Math.abs(amount) >= 1000000) {
    return `$${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
  }

  if (Math.abs(amount) >= 1000) {
    return `$${Math.round(amount / 1000)}k`;
  }

  return formatUsd(amount);
};

const formatPercent = (value = 0) => `${Number(value || 0).toFixed(2)}%`;
const formatSignedPercent = (value = 0) => `${Number(value) > 0 ? '+' : ''}${formatPercent(value)}`;
const formatRate = (value = 0) => Number(value || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 4 });

const todayIsoDate = () => {
  const now = new Date();
  return [now.getFullYear(), now.getMonth() + 1, now.getDate()]
    .map((part) => String(part).padStart(2, '0'))
    .join('-');
};
const formatIndexValue = (value = 0) => Number(value || 0).toFixed(2);
const formatDateTime = (value, language) => (
  value ? new Date(value).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US') : '-'
);
const latestDateValue = (...values) => (
  values
    .filter(Boolean)
    .sort((valueA, valueB) => new Date(valueB) - new Date(valueA))[0] || null
);
const formatFileSize = (bytes = 0) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${Math.max(Math.round(bytes / 1024), 1)} KB`;
};

const formatStorageUsage = (usage, storage) => {
  if (!usage) {
    return storage || '-';
  }

  const used = formatFileSize(usage.usedBytes || 0);
  const limit = usage.limitBytes ? formatFileSize(usage.limitBytes) : null;
  const percent = usage.usedPercent !== null && usage.usedPercent !== undefined
    ? ` (${usage.usedPercent}%)`
    : '';

  return limit ? `${used} / ${limit}${percent}` : used;
};

const translations = {
  cs: {
    adminDashboard: 'Administrace fondu',
    partnerDashboard: 'Partnerský účet',
    fullOverview: 'Přehled celého fondu',
    partnerAccount: 'Váš účet ve fondu',
    logout: 'Odhlásit',
    loading: 'Načítám...',
    failedLoad: 'Nepodařilo se načíst data',
    failedUpdate: 'Nepodařilo se upravit kapitál',
    switchToEnglish: 'English',
    theme: 'Vzhled',
    darkTheme: 'Tmavý',
    lightTheme: 'Světlý',
    totalFundValue: 'Celková hodnota fondu',
    totalContributions: 'Celkové vklady',
    partners: 'Partneři',
    fundCapitalGrowth: 'Vývoj kapitálu fondu',
    fundCzkGrowth: 'Vývoj hodnoty fondu v CZK',
    fundNetFlows: 'Čisté vklady a výběry fondu',
    fundNetFlowsHelp: 'Součet všech vkladů a výběrů partnerů v daném pololetí. Nezahrnuje zhodnocení.',
    netFlowTotal: 'Celkem čistě',
    inflow: 'Vklady',
    outflow: 'Výběry',
    noNetFlows: 'Žádné vklady ani výběry nejsou evidované',
    partnerAccounts: 'Partnerské účty',
    name: 'Jméno',
    email: 'Email',
    capital: 'Kapitál',
    capitalUsd: 'Kapitál USD',
    contributed: 'Vloženo',
    share: 'Podíl',
    lastLogin: 'Poslední přihlášení',
    lastRefresh: 'Poslední refresh',
    lastSeen: 'Poslední login/refresh',
    activityLastHalfYear: 'Login/refresh za poslední půlrok',
    loginHistory: 'Historie přihlášení',
    noLoginHistory: 'Zatím žádné přihlášení',
    actions: 'Akce',
    viewAsPartner: 'Zobrazit jako partner',
    stopViewingAsPartner: 'Zpět do adminu',
    viewingAsPartner: 'Zobrazuješ partnerský účet jako admin',
    resetPassword: 'Reset hesla',
    createPartner: 'Přidat partnera',
    createPartnerHelp: 'Jméno musí odpovídat řádku v Excelu, aby se při importu správně spárovaly hodnoty.',
    partnerCreated: 'Partner byl vytvořen',
    confirmDeletePartner: 'Opravdu smazat tohoto partnera? Pokud zůstane v Excelu, další import ho znovu vytvoří.',
    hideCapital: 'Skrýt kapitál',
    showCapital: 'Zobrazit kapitál',
    temporaryPassword: 'Dočasné heslo',
    temporaryPasswordHelp: 'Zkopírujte ho partnerovi. Po zavření se znovu nezobrazí.',
    confirmPasswordReset: 'Opravdu vygenerovat nové heslo pro tohoto partnera?',
    save: 'Uložit',
    cancel: 'Zrušit',
    detail: 'Detail',
    closeDetail: 'Zavřít detail',
    importExcel: 'Import Excelu',
    previewExcel: 'Zkontrolovat Excel',
    confirmImport: 'Potvrdit import',
    lastImportPeriod: 'Poslední období',
    statementDate: 'Datum výpisu',
    statementDateHint: 'Volitelné datum zobrazené v portálu a exportech. Čtení Excelu pořád používá vybrané období.',
    customImportPeriod: 'Ruční datum období',
    customImportPeriodHint: 'Např. 9/30/30, pokud Excel pokračuje stejným rozložením sloupců.',
    importArchive: 'Archiv importů',
    detectedImportPeriods: 'Období nalezená v Excelu',
    interimTitle: 'Průběžná aktualizace bez Excelu',
    interimHelp: 'Zadejte hrubé zhodnocení fondu (před poplatkem) od posledního výpisu a kurz USD/CZK. Kapitál, podíly a odhad poplatku všech partnerů se přepočítají stejně jako v Excelu: 25 % ze zisku nad 6 % za fondový rok. Nové číslo vždy nahradí předchozí průběžnou aktualizaci, import Excelu ji zruší.',
    interimNoBasis: 'Zatím není naimportovaný žádný výpis z Excelu.',
    interimLastStatement: 'Poslední výpis',
    interimYtdAtStatement: 'Hrubé zhodnocení fondového roku k výpisu',
    interimReturn: 'Zhodnocení od výpisu (%)',
    interimReturnHint: 'Hrubé, před poplatkem. Např. 2 nebo -1,5',
    interimRate: 'Kurz USD/CZK',
    interimDate: 'Datum ocenění',
    interimDateHint: 'Musí být po posledním výpisu a před dalším',
    interimPreview: 'Spočítat náhled',
    interimApply: 'Použít pro všechny partnery',
    interimRevert: 'Zrušit průběžnou aktualizaci',
    interimConfirmRevert: 'Vrátit všechny hodnoty na poslední importovaný výpis?',
    interimActive: 'Aktivní průběžná aktualizace',
    interimApplied: 'Průběžná aktualizace uložena',
    interimFundTotal: 'Celkem fond',
    interimNetYtd: 'Čisté zhodnocení fondového roku',
    interimPendingFees: 'Odhad poplatků za fondový rok',
    interimFeeExempt: 'bez poplatku',
    interimReceivesFees: 'přijímá poplatky',
    pendingFee: 'Odhad poplatku za fondový rok',
    pendingFeeHint: 'Zatím neúčtován, konečná výše se určí k 31. 3.',
    downloadBackup: 'Stáhnout backup',
    adminStatus: 'Stav portálu',
    storage: 'Úložiště',
    latestImport: 'Poslední import',
    latestBackup: 'Poslední backup',
    resetRequests: 'Žádosti o reset hesla',
    twoFactor: '2FA pro admina',
    setup2fa: 'Nastavit 2FA',
    enable2fa: 'Zapnout 2FA',
    twoFactorCode: 'Kód z aplikace',
    disclaimer: 'Hodnoty v portálu jsou informativní a vycházejí z posledního importovaného výpisu.',
    exportCsv: 'Export CSV',
    exportMyResults: 'Exportovat moje výsledky',
    exportMyResultsCsv: 'Stáhnout CSV',
    exportMyResultsExcel: 'Stáhnout Excel',
    partnerExportHelp: 'Stáhne jednoduchý osobní Excel nebo CSV s kapitálem, vklady, historií a poplatky.',
    adminTools: 'Admin nástroje',
    demoAccountTitle: 'Ukázkový účet pro zájemce',
    demoAccountHelp: 'Přihlašovací údaje pro potenciální partnery, ať vidí, jak portál vypadá. Nezapočítává se do celkového kapitálu ani jiných součtů fondu, heslo si u něj nikdo nemůže změnit.',
    demoAccountPassword: 'Heslo',
    auditLog: 'Audit log',
    documentDownloads: 'Stažení dokumentů',
    noDocumentDownloads: 'Zatím si nikdo nestáhl žádný dokument',
    noAudit: 'Zatím zde nejsou žádné události',
    partnerDetail: 'Detail partnera',
    currentCapital: 'Aktuální kapitál',
    totalContributed: 'Celkem vloženo',
    netGain: 'Čistý zisk',
    yourFundShare: 'Váš podíl ve fondu',
    fundTotalCapital: 'Celkový kapitál fondu',
    yourNetWorthGrowth: 'Vývoj vašeho majetku',
    yourContributionsByYear: 'Vaše vklady podle roku',
    yourContributions: 'Vaše vklady',
    amountCzk: 'Částka CZK',
    amountUsd: 'Částka USD',
    capitalGrowth: 'Vývoj kapitálu',
    date: 'Datum',
    notEnoughHistory: 'Zatím není dost historie',
    noContributions: 'Žádné vklady nejsou evidované',
    periodContributions: 'Vklady v období',
    hundredInvestedGrowth: 'Čistá výkonnost fondu od roku 2020',
    irrFromStart: 'IRR od roku 2020',
    total: 'celkem',
    latest: 'Poslední hodnota',
    language: 'Jazyk',
    accountSettings: 'Nastavení účtu',
    currentPassword: 'Současné heslo',
    newPassword: 'Nové heslo',
    showPassword: 'Zobrazit hesla',
    hidePassword: 'Skrýt hesla',
    changePassword: 'Změnit heslo',
    passwordUpdated: 'Heslo bylo změněno',
    shareholderLetters: 'Dopisy partnerům',
    uploadLetter: 'Nahrát dopis',
    download: 'Stáhnout',
    delete: 'Smazat',
    noDocuments: 'Zatím nejsou nahrané žádné dokumenty',
    fundFees: 'Vybrané poplatky fondu',
    partnerFees: 'Vaše zaplacené poplatky',
    totalFees: 'Celkem vyplaceno',
    yearlyFees: 'Poplatky podle roku',
    fundRules: 'Pravidla fondu',
    fundRulesItems: [
      'Poplatek je 1/4 ze zisku nad 6 % p.a.',
      'Pokud se fondu v daném roce nepodaří překonat 6 %, poplatek se pro další rok snižuje na 10 % ze zisku nad 6 %.',
      'Výpis fondu vychází vždy k 15. 3. a 15. 9.',
      'Do 20. 3. dává partner vědět, kolik chce vybrat nebo vložit; do 20. 9. pouze kolik chce vložit.',
      'Do 25. 3. a 25. 9. posílá partner peníze na účet správce fondu.',
      'Vklady a výběry se zapisují vždy k účetnímu dni jednou za pololetí, a to poslední den v 3. a 9. měsíci.',
      'Peníze je možné přidávat k oběma pololetním termínům.',
      'Peníze je možné vybrat pouze na konci roku fondu, tedy v 3. měsíci.',
      'V mimořádných životních situacích si může každý partner vybrat svůj podíl okamžitě.',
      'Všechna rozhodnutí o alokaci kapitálu a investicích dělá pouze portfolio manažer a ve volbě investic není omezen.'
    ],
    emergencyContact: 'Emergency kontakt',
    emergencyContactHelp: 'Uveďte svůj emergency kontakt.',
    emergencyContactName: 'Jméno osoby',
    emergencyContactRelationship: 'Vztah k vám',
    emergencyContactEmail: 'Email kontaktu',
    emergencyContactPhone: 'Telefon kontaktu',
    emergencyContactNote: 'Poznámka nebo instrukce',
    emergencyContactUpdated: 'Emergency kontakt byl uložen',
    noEmergencyContact: 'Emergency kontakt zatím není vyplněný',
    fundContacts: 'Důležité kontakty',
    fundContactManager: 'Správce fondu — Vojtěch Naar',
    fundContactManagerHelp: 'Dotazy k fondu nebo k vašemu podílu.',
    fundContactContinuity: 'Emergency kontakt',
    fundContactContinuityHelp: 'Jana Naarová',
    fundUpdates: 'Aktuálně ve fondu',
    fundUpdateTitle: 'Krátký nadpis',
    fundUpdatePlaceholder: 'Napište krátký status o tom, co se děje ve fondu nebo na trzích...',
    fundUpdateTitleEn: 'Nadpis anglicky (nepovinné)',
    fundUpdatePlaceholderEn: 'Anglická verze statusu (nepovinná) — když zůstane prázdná, zobrazí se všem česká.',
    editUpdate: 'Upravit',
    publishUpdate: 'Publikovat status',
    noFundUpdates: 'Zatím zde nejsou žádné statusy',
    compoundCalculator: 'Kalkulačka složeného úroku',
    compoundCalculatorHelp: 'Modelový výpočet pro pololetní vklady do fondu podle zadaného reálného výnosu. Cílem portfolia je překonávat 12 %. Optimistický/pesimistický scénář počítá s +4/-4 %.',
    startingCapital: 'Počáteční kapitál',
    halfYearContribution: 'Pololetní vklad',
    expectedReturn: 'Reálný roční výnos',
    investmentYears: 'Počet let',
    baseScenario: 'Základní scénář',
    optimisticScenario: 'Optimistický scénář',
    pessimisticScenario: 'Pesimistický scénář',
    totalInvested: 'Celkem vloženo',
  },
  en: {
    adminDashboard: 'Admin Dashboard',
    partnerDashboard: 'Partner Dashboard',
    fullOverview: 'Full fund overview',
    partnerAccount: 'Your fund account',
    logout: 'Logout',
    loading: 'Loading...',
    failedLoad: 'Failed to load data',
    failedUpdate: 'Failed to update capital',
    switchToEnglish: 'Cestina',
    theme: 'Theme',
    darkTheme: 'Dark',
    lightTheme: 'Light',
    totalFundValue: 'Total Fund Value',
    totalContributions: 'Total Contributions',
    partners: 'Partners',
    fundCapitalGrowth: 'Fund Capital Growth',
    fundCzkGrowth: 'Fund CZK Value Growth',
    fundNetFlows: 'Fund net deposits and withdrawals',
    fundNetFlowsHelp: 'Sum of all partner deposits and withdrawals in each half-year. Excludes investment returns.',
    netFlowTotal: 'Net total',
    inflow: 'Deposits',
    outflow: 'Withdrawals',
    noNetFlows: 'No deposits or withdrawals recorded',
    partnerAccounts: 'Partner Accounts',
    name: 'Name',
    email: 'Email',
    capital: 'Capital',
    capitalUsd: 'Capital USD',
    contributed: 'Contributed',
    share: 'Share',
    lastLogin: 'Last login',
    lastRefresh: 'Last refresh',
    lastSeen: 'Last login/refresh',
    activityLastHalfYear: 'Login/refresh in the last half-year',
    loginHistory: 'Login history',
    noLoginHistory: 'No logins yet',
    actions: 'Actions',
    viewAsPartner: 'View as partner',
    stopViewingAsPartner: 'Back to admin',
    viewingAsPartner: 'Viewing partner account as admin',
    resetPassword: 'Reset password',
    createPartner: 'Add partner',
    createPartnerHelp: 'The name should match the Excel row so imported values are assigned correctly.',
    partnerCreated: 'Partner created',
    confirmDeletePartner: 'Delete this partner? If they remain in Excel, the next import will create them again.',
    hideCapital: 'Hide capital',
    showCapital: 'Show capital',
    temporaryPassword: 'Temporary password',
    temporaryPasswordHelp: 'Send this to the partner. It will not be shown again after closing.',
    confirmPasswordReset: 'Generate a new password for this partner?',
    save: 'Save',
    cancel: 'Cancel',
    detail: 'Detail',
    closeDetail: 'Close detail',
    importExcel: 'Excel import',
    previewExcel: 'Check Excel',
    confirmImport: 'Confirm import',
    lastImportPeriod: 'Last period',
    statementDate: 'Statement date',
    statementDateHint: 'Optional date shown in the portal and exports. Excel reading still uses the selected period.',
    customImportPeriod: 'Manual period date',
    customImportPeriodHint: 'Example: 9/30/30 if the Excel keeps the same column layout.',
    importArchive: 'Import archive',
    detectedImportPeriods: 'Periods found in Excel',
    interimTitle: 'Interim update without Excel',
    interimHelp: 'Enter the fund\'s gross return (before fees) since the last statement and the USD/CZK rate. Every partner\'s capital, share and estimated fee are recalculated the same way as in the Excel: 25 % of the gain above 6 % per fund year. A new number always replaces the previous interim update; an Excel import clears it.',
    interimNoBasis: 'No Excel statement has been imported yet.',
    interimLastStatement: 'Last statement',
    interimYtdAtStatement: 'Gross fund-year return at statement',
    interimReturn: 'Return since statement (%)',
    interimReturnHint: 'Gross, before fees. E.g. 2 or -1.5',
    interimRate: 'USD/CZK rate',
    interimDate: 'Valuation date',
    interimDateHint: 'Must be after the last statement and before the next one',
    interimPreview: 'Calculate preview',
    interimApply: 'Apply to all partners',
    interimRevert: 'Clear interim update',
    interimConfirmRevert: 'Restore all values to the latest imported statement?',
    interimActive: 'Active interim update',
    interimApplied: 'Interim update saved',
    interimFundTotal: 'Fund total',
    interimNetYtd: 'Net fund-year return',
    interimPendingFees: 'Estimated fees for the fund year',
    interimFeeExempt: 'no fee',
    interimReceivesFees: 'receives fees',
    pendingFee: 'Estimated fee for the fund year',
    pendingFeeHint: 'Not charged yet; the final amount is set on 3/31.',
    downloadBackup: 'Download backup',
    adminStatus: 'Portal status',
    storage: 'Storage',
    latestImport: 'Latest import',
    latestBackup: 'Latest backup',
    resetRequests: 'Password reset requests',
    twoFactor: 'Admin 2FA',
    setup2fa: 'Set up 2FA',
    enable2fa: 'Enable 2FA',
    twoFactorCode: 'Code from app',
    disclaimer: 'Values in the portal are informational and based on the latest imported statement.',
    exportCsv: 'Export CSV',
    exportMyResults: 'Export my results',
    exportMyResultsCsv: 'Download CSV',
    exportMyResultsExcel: 'Download Excel',
    partnerExportHelp: 'Downloads a simple personal Excel or CSV with capital, contributions, history, and fees.',
    adminTools: 'Admin tools',
    demoAccountTitle: 'Demo account for prospects',
    demoAccountHelp: 'Login for potential partners so they can see what the portal looks like. Not counted in total fund capital or other fund totals, and its password cannot be changed.',
    demoAccountPassword: 'Password',
    auditLog: 'Audit log',
    documentDownloads: 'Document downloads',
    noDocumentDownloads: 'No document downloads yet',
    noAudit: 'No events yet',
    partnerDetail: 'Partner detail',
    currentCapital: 'Current Capital',
    totalContributed: 'Total Contributed',
    netGain: 'Net Gain',
    yourFundShare: 'Your Share of the Fund',
    fundTotalCapital: 'Total Fund Capital',
    yourNetWorthGrowth: 'Your Net Worth Growth',
    yourContributionsByYear: 'Your Contributions By Year',
    yourContributions: 'Your Contributions',
    amountCzk: 'Amount CZK',
    amountUsd: 'Amount USD',
    capitalGrowth: 'Capital Growth',
    date: 'Date',
    notEnoughHistory: 'Not enough history yet',
    noContributions: 'No contributions recorded',
    periodContributions: 'Period contributions',
    hundredInvestedGrowth: 'Net fund performance from 2020',
    irrFromStart: 'IRR from 2020',
    total: 'total',
    latest: 'Latest value',
    language: 'Language',
    accountSettings: 'Account Settings',
    currentPassword: 'Current password',
    newPassword: 'New password',
    showPassword: 'Show passwords',
    hidePassword: 'Hide passwords',
    changePassword: 'Change password',
    passwordUpdated: 'Password updated',
    shareholderLetters: 'Shareholder Letters',
    uploadLetter: 'Upload letter',
    download: 'Download',
    delete: 'Delete',
    noDocuments: 'No documents uploaded yet',
    fundFees: 'Collected fund fees',
    partnerFees: 'Your paid fees',
    totalFees: 'Total paid',
    yearlyFees: 'Fees by year',
    fundRules: 'Fund rules',
    fundRulesItems: [
      'The fee is 1/4 of profit above 6% p.a.',
      'If the fund does not beat 6% in a given year, the fee for the next year is reduced to 10% of profit above 6%.',
      'Fund statements are issued on 15. 3. and 15. 9.',
      'By 20. 3., the partner announces how much they want to withdraw or deposit; by 20. 9., only how much they want to deposit.',
      'By 25. 3. and 25. 9., the partner sends the money to the fund manager\'s account.',
      'Deposits and withdrawals are recorded on the accounting date once per half-year, on the last day of the 3rd and 9th month.',
      'Money can be added on both semiannual dates.',
      'Money can be withdrawn only at the end of the fund year, in the 3rd month.',
      'In extraordinary life circumstances, any partner can withdraw their share immediately.',
      'All capital allocation and investment decisions are made solely by the portfolio manager, with no restrictions on investment selection.'
    ],
    emergencyContact: 'Emergency contact',
    emergencyContactHelp: 'Add your emergency contact.',
    emergencyContactName: 'Contact name',
    emergencyContactRelationship: 'Relationship',
    emergencyContactEmail: 'Contact email',
    emergencyContactPhone: 'Contact phone',
    emergencyContactNote: 'Note or instructions',
    emergencyContactUpdated: 'Emergency contact saved',
    noEmergencyContact: 'No emergency contact entered yet',
    fundContacts: 'Important contacts',
    fundContactManager: 'Fund manager — Vojtěch Naar',
    fundContactManagerHelp: 'Questions about the fund or your share.',
    fundContactContinuity: 'Emergency contact',
    fundContactContinuityHelp: 'Jana Naarová',
    fundUpdates: 'Fund updates',
    fundUpdateTitle: 'Short title',
    fundUpdatePlaceholder: 'Write a short update about what is happening in the fund or markets...',
    fundUpdateTitleEn: 'Title in English (optional)',
    fundUpdatePlaceholderEn: 'English version (optional) — if left empty, the Czech text is shown to everyone.',
    editUpdate: 'Edit',
    publishUpdate: 'Publish update',
    noFundUpdates: 'No updates yet',
    compoundCalculator: 'Compound interest calculator',
    compoundCalculatorHelp: 'Model projection for semiannual fund contributions using the entered real return. The portfolio target is to exceed 12%. The optimistic/pessimistic scenario uses +4/-4%.',
    startingCapital: 'Starting capital',
    halfYearContribution: 'Semiannual contribution',
    expectedReturn: 'Real yearly return',
    investmentYears: 'Years',
    baseScenario: 'Base scenario',
    optimisticScenario: 'Optimistic scenario',
    pessimisticScenario: 'Pessimistic scenario',
    totalInvested: 'Total invested',
  }
};

const normalizeAmericanDate = (date) => {
  const value = String(date || '').trim();
  const match = value.match(/^(\d{1,2})[./]\s*(\d{1,2})[./]\s*(\d{2}|\d{4})$/);

  if (!match) {
    return value;
  }

  const separator = value.includes('.') ? '.' : '/';
  const first = Number(match[1]);
  const second = Number(match[2]);
  const year = Number(match[3]);
  const isEuropeanInput = separator === '.' || first > 12;
  const month = isEuropeanInput ? second : first;
  const day = isEuropeanInput ? first : second;
  const shortYear = String(year < 100 ? year : year % 100).padStart(2, '0');

  if (!month || !day || month > 12 || day > 31) {
    return value;
  }

  return `${month}/${day}/${shortYear}`;
};

const parseDate = (date) => {
  const normalizedDate = normalizeAmericanDate(date);
  const [month, day, year] = String(normalizedDate || '').split('/').map(Number);
  if (!month || !day || !year) {
    return null;
  }

  return new Date(year < 100 ? 2000 + year : year, month - 1, day);
};

const yearsBetween = (start, end) => (end - start) / (365.25 * 24 * 60 * 60 * 1000);

const getYearFromDate = (date) => {
  const value = String(date || '');
  const match = value.match(/(?:19|20)\d{2}|\d{2}$/);

  if (!match) {
    return 'Unknown';
  }

  const year = match[0];
  return year.length === 2 ? `20${year}` : year;
};

const yearlyContributions = (deposits = []) => {
  const totals = deposits.reduce((byYear, deposit) => {
    const year = getYearFromDate(deposit.date);
    if (!byYear[year]) {
      byYear[year] = { amountCzk: 0, amountUsd: 0 };
    }
    byYear[year].amountCzk += deposit.amountCzk || 0;
    byYear[year].amountUsd += deposit.amountUsd || 0;
    return byYear;
  }, {});

  return Object.entries(totals)
    .sort(([yearA], [yearB]) => yearA.localeCompare(yearB))
    .map(([date, amounts]) => ({ date, ...amounts }));
};

const sumDeposits = (deposits = [], key = 'amountCzk') =>
  deposits.reduce((sum, deposit) => sum + Number(deposit[key] || 0), 0);

// Rolls every partner's deposits up into one row per settlement date, keeping
// money in and money out apart so the chart can show the net move as a bar
// and still name both sides in its tooltip. Withdrawals are stored as
// negative deposits, which is what separates the two here.
const fundFlowsByPeriod = (partners = []) => {
  const byDate = partners.reduce((totals, partner) => {
    (partner.deposits || []).forEach((deposit) => {
      const date = deposit.date;
      if (!totals[date]) {
        totals[date] = {
          date,
          netCzk: 0,
          netUsd: 0,
          inflowCzk: 0,
          inflowUsd: 0,
          outflowCzk: 0,
          outflowUsd: 0
        };
      }

      const amountCzk = Number(deposit.amountCzk || 0);
      const amountUsd = Number(deposit.amountUsd || 0);
      totals[date].netCzk += amountCzk;
      totals[date].netUsd += amountUsd;
      totals[date][amountCzk < 0 ? 'outflowCzk' : 'inflowCzk'] += amountCzk;
      totals[date][amountUsd < 0 ? 'outflowUsd' : 'inflowUsd'] += amountUsd;
    });

    return totals;
  }, {});

  return Object.values(byDate)
    .sort((entryA, entryB) => (parseDate(entryA.date) || 0) - (parseDate(entryB.date) || 0));
};

const emptyEmergencyContact = () => ({
  name: '',
  relationship: '',
  email: '',
  phone: '',
  note: ''
});

const hasEmergencyContact = (contact = {}) => (
  Boolean(
    String(contact.name || '').trim()
    || String(contact.relationship || '').trim()
    || String(contact.email || '').trim()
    || String(contact.phone || '').trim()
    || String(contact.note || '').trim()
  )
);

const contributionsByHistoryPeriod = (history = [], deposits = []) => {
  const orderedHistory = [...history]
    .map((entry) => ({ ...entry, parsedDate: parseDate(entry.date) }))
    .filter((entry) => entry.parsedDate)
    .sort((entryA, entryB) => entryA.parsedDate - entryB.parsedDate);
  const datedDeposits = deposits
    .map((deposit) => ({
      ...deposit,
      parsedDate: parseDate(deposit.date),
      amountCzk: deposit.amountCzk || 0,
      amountUsd: deposit.amountUsd || 0
    }))
    .filter((deposit) => deposit.parsedDate && (deposit.amountCzk !== 0 || deposit.amountUsd !== 0));

  return orderedHistory.map((entry, index) => {
    const previousDate = index > 0 ? orderedHistory[index - 1].parsedDate : null;
    const totals = datedDeposits.reduce((sum, deposit) => {
      const isAfterPrevious = previousDate ? deposit.parsedDate > previousDate : deposit.parsedDate <= entry.parsedDate;
      const isInPeriod = isAfterPrevious && deposit.parsedDate <= entry.parsedDate;
      if (!isInPeriod) {
        return sum;
      }

      return {
        amountCzk: sum.amountCzk + deposit.amountCzk,
        amountUsd: sum.amountUsd + deposit.amountUsd
      };
    }, { amountCzk: 0, amountUsd: 0 });

    return {
      date: entry.date,
      ...totals
    };
  });
};

const buildHundredInvestedGrowth = (history = [], valueKey = 'capitalCzk') => {
  const points = history
    .map((entry) => ({
      date: entry.date,
      parsedDate: parseDate(entry.date),
      value: Number(entry[valueKey] || 0)
    }))
    .filter((entry) => entry.parsedDate && entry.value > 0)
    .sort((entryA, entryB) => entryA.parsedDate - entryB.parsedDate);

  const first = points[0];
  if (!first) {
    return [];
  }

  return points.map((entry) => ({
    date: entry.date,
    indexValue: (entry.value / first.value) * 100
  }));
};

const filterFeesThroughLatestHistory = (fees = [], history = []) => {
  const latestHistory = [...history]
    .map((entry) => ({ ...entry, parsedDate: parseDate(entry.date) }))
    .filter((entry) => entry.parsedDate && ((entry.capitalUsd || 0) > 0 || (entry.capitalCzk || 0) > 0))
    .sort((entryA, entryB) => entryB.parsedDate - entryA.parsedDate)[0];

  if (!latestHistory) {
    return fees;
  }

  return fees.filter((fee) => {
    const feeDate = parseDate(fee.date);
    return feeDate && feeDate <= latestHistory.parsedDate;
  });
};

const applyStatementDateToLatestHistory = (history = [], statementDate) => {
  if (!statementDate || !history.length) {
    return history;
  }

  const displayDate = normalizeAmericanDate(statementDate);
  let latestIndex = -1;
  history.forEach((entry, index) => {
    if ((entry.capitalUsd || 0) > 0 || (entry.capitalCzk || 0) > 0) {
      latestIndex = index;
    }
  });

  if (latestIndex === -1) {
    return history;
  }

  return history.map((entry, index) => (
    index === latestIndex ? { ...entry, displayDate } : entry
  ));
};

const calculateAnnualizedReturn = (growth = []) => {
  const first = growth[0];
  const latest = growth[growth.length - 1];

  if (!first || !latest) {
    return null;
  }

  const firstDate = parseDate(first.date);
  const latestDate = parseDate(latest.displayDate || latest.date);
  if (!firstDate || !latestDate || latest.indexValue <= 0 || first.indexValue <= 0) {
    return null;
  }

  const years = yearsBetween(firstDate, latestDate);
  if (years <= 0) {
    return null;
  }

  return (((latest.indexValue / first.indexValue) ** (1 / years)) - 1) * 100;
};

// Gaps shorter than this share of the typical gap are stretched up to it.
const MIN_GAP_SHARE = 0.4;
// Roughly one "12/31/26" date label wide, in SVG viewBox units.
const MIN_DATE_LABEL_GAP = 56;

// Places points along the x-axis (0..1) by the time between them, so a
// statement dated a few days after the last half-year close sits close to it
// instead of a full half-year slot away. Each gap is floored at a share of
// the typical gap, though, so two snapshots days apart stay readable.
const timeProportionalPositions = (dates) => {
  const evenlySpaced = dates.map((_, index) => index / Math.max(dates.length - 1, 1));
  if (dates.some((date) => !date)) {
    return evenlySpaced;
  }

  const gaps = dates.slice(1).map((date, index) => Math.max(date - dates[index], 0));
  const typicalGap = [...gaps].sort((gapA, gapB) => gapA - gapB)[Math.floor(gaps.length / 2)];
  if (!typicalGap) {
    return evenlySpaced;
  }

  const flooredGaps = gaps.map((gap) => Math.max(gap, typicalGap * MIN_GAP_SHARE));
  const total = flooredGaps.reduce((sum, gap) => sum + gap, 0);
  let elapsed = 0;

  return [0, ...flooredGaps.map((gap) => {
    elapsed += gap;
    return elapsed / total;
  })];
};

const SimpleLineChart = ({
  title,
  data,
  valueKey,
  formatValue,
  summary,
  emptyText,
  color = '#1b6b5f',
  contributionData = [],
  contributionTitle = '',
  compactFormatValue,
  formatContribution = formatCzk,
  contributionValueKey = 'amountCzk',
  controls = null,
  showPointLabels = true
}) => {
  const points = (data || [])
    .map((entry) => ({
      label: entry.date,
      displayLabel: entry.displayDate || entry.statementDate || entry.date,
      isInterim: Boolean(entry.interim),
      value: Number(entry[valueKey] || 0)
    }))
    .filter((entry) => Number.isFinite(entry.value) && entry.value >= 0);

  if (points.length < 2) {
    return (
      <div className="chart-panel">
        <h2>{title}</h2>
        <p className="empty-chart">{emptyText}</p>
      </div>
    );
  }

  const displayValue = compactFormatValue || formatValue;
  const width = 1320;
  const height = 380;
  const totalHeight = height;
  const paddingX = 150;
  const paddingTop = 84;
  const paddingBottom = 58;
  const min = Math.min(...points.map((point) => point.value));
  const max = Math.max(...points.map((point) => point.value));
  const range = max - min || 1;

  const xShares = timeProportionalPositions(points.map((point) => parseDate(point.displayLabel)));
  const coordinates = points.map((point, index) => {
    const x = paddingX + xShares[index] * (width - paddingX * 2);
    const y = height - paddingBottom - ((point.value - min) / range) * (height - paddingTop - paddingBottom);
    return { ...point, x, y };
  });
  const dateLabelStep = Math.max(1, Math.ceil(points.length / 8));
  // Every few dates plus the first and last, skipping any that would overlap
  // the one before; the last (the statement date) wins over its neighbour.
  const labeledIndexes = new Set(coordinates.reduce((kept, point, index) => {
    const isLast = index === coordinates.length - 1;
    if (index !== 0 && !isLast && index % dateLabelStep !== 0) {
      return kept;
    }

    const previous = kept[kept.length - 1];
    if (previous !== undefined && point.x - coordinates[previous].x < MIN_DATE_LABEL_GAP) {
      return isLast ? [...kept.slice(0, -1), index] : kept;
    }

    return [...kept, index];
  }, []));

  const path = coordinates
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
  const contributionByDate = contributionData.reduce((byDate, entry) => {
    byDate[entry.date] = Number(entry[contributionValueKey] || 0);
    return byDate;
  }, {});
  const leftOffset = (paddingX / width) * 100;
  const plotWidth = 100 - leftOffset * 2;
  // A statement-date or interim point is only a mid-period valuation, where
  // no money can be added, so it gets no column unless it does carry a deposit.
  const contributionBars = points
    .map((point, index) => ({
      ...point,
      contribution: contributionByDate[point.label] || 0,
      left: leftOffset + xShares[index] * plotWidth
    }))
    .filter((point) => point.contribution !== 0 || (point.displayLabel === point.label && !point.isInterim));
  const maxContribution = Math.max(...contributionBars.map((point) => point.contribution), 0);
  // Columns are as wide as the closest neighbour allows (up to 64px), so
  // dates that sit close together never overlap.
  const columnWidthShare = (index) => {
    const gaps = [contributionBars[index - 1], contributionBars[index + 1]]
      .filter(Boolean)
      .map((neighbour) => Math.abs(neighbour.left - contributionBars[index].left));
    return gaps.length > 0 ? Math.min(...gaps) * 0.9 : plotWidth;
  };

  return (
    <div className="chart-panel">
      <div className="chart-header">
        <h2>{title}</h2>
        <div className="chart-header-right">
          {summary && <span>{summary}</span>}
          {controls}
        </div>
      </div>
      <div className="chart-scroll">
        <div className="chart-scroll-inner" style={{ '--chart-min-width': `${200 + points.length * 78}px` }}>
      <svg viewBox={`0 0 ${width} ${totalHeight}`} role="img" aria-label={title}>
        <line x1={paddingX} y1={height - paddingBottom} x2={width - paddingX} y2={height - paddingBottom} className="chart-axis" />
        <line x1={paddingX} y1={paddingTop} x2={paddingX} y2={height - paddingBottom} className="chart-axis" />
        {coordinates.map((point) => (
          <line
            key={`guide-${point.label}`}
            x1={point.x}
            y1={paddingTop}
            x2={point.x}
            y2={height - paddingBottom}
            className="chart-guide"
          />
        ))}
        {coordinates.map((point, index) => (
          labeledIndexes.has(index) && (
            <text
              key={`date-${point.label}`}
              x={point.x}
              y={height - paddingBottom + 34}
              textAnchor="middle"
              className="chart-date-label"
            >
              {point.displayLabel}
            </text>
          )
        ))}
        <path d={path} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {coordinates.map((point, index) => (
          <g key={`${point.label}-${point.x}`}>
            <circle cx={point.x} cy={point.y} r="5" fill={color}>
              <title>{`${point.displayLabel}: ${formatValue(point.value)}`}</title>
            </circle>
            {showPointLabels && (
              <g transform={`translate(${point.x} ${Math.max(index % 2 === 0 ? point.y - 28 : point.y + 30, 24)})`}>
                <rect x="-48" y="-15" width="96" height="24" rx="5" className="chart-point-badge" />
                <text y="2" textAnchor="middle" className="chart-point-value">
                  {formatValue(point.value)}
                </text>
              </g>
            )}
          </g>
        ))}
        <text x={paddingX + 4} y={paddingTop - 18} className="chart-value">{displayValue(max)}</text>
        <text x={paddingX + 4} y={height - paddingBottom - 10} className="chart-value">{displayValue(min)}</text>
      </svg>
      {contributionData.length > 0 && (
        <div className="contribution-timeline">
          <div className="contribution-timeline-title">{contributionTitle}</div>
          <div className="contribution-columns">
            {contributionBars.map((point, index) => {
              const barHeight = point.contribution > 0
                ? Math.max((point.contribution / (maxContribution || 1)) * 100, 8)
                : 0;

              return (
                <div
                  className="contribution-column"
                  key={`contribution-${point.label}`}
                  style={{ left: `${point.left}%`, width: `min(64px, ${columnWidthShare(index)}%)` }}
                >
                  <div className={`contribution-amount${point.contribution < 0 ? ' negative-value' : ''}`}>
                    {point.contribution !== 0 ? formatContribution(point.contribution) : '-'}
                  </div>
                  <div className="contribution-track">
                    <div
                      className="contribution-fill"
                      style={{ height: `${barHeight}%` }}
                      title={`${point.displayLabel}: ${formatContribution(point.contribution)}`}
                    />
                  </div>
                  <div className="contribution-date">{point.displayLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
        </div>
      </div>

    </div>
  );
};

// Signed column chart around a zero baseline: deposits rise above it,
// withdrawals drop below. Unlike SimpleBarChart (a horizontal list of
// positive magnitudes) it has to keep the sign visible, since a half-year
// where partners took more out than they put in is exactly what this is for.
const NetFlowChart = ({
  title,
  subtitle,
  data,
  valueKey,
  inflowKey,
  outflowKey,
  formatValue,
  compactFormatValue,
  emptyText,
  summaryLabel,
  inflowLabel,
  outflowLabel,
  controls = null,
  positiveColor = '#34d399',
  negativeColor = '#fb7185'
}) => {
  const bars = (data || [])
    .map((entry) => ({
      label: entry.date,
      displayLabel: entry.displayDate || entry.date,
      value: Number(entry[valueKey] || 0),
      inflow: Number(entry[inflowKey] || 0),
      outflow: Number(entry[outflowKey] || 0)
    }))
    .filter((entry) => Number.isFinite(entry.value));

  if (!bars.length) {
    return (
      <div className="chart-panel">
        <h2>{title}</h2>
        <p className="empty-chart">{emptyText}</p>
      </div>
    );
  }

  const displayValue = compactFormatValue || formatValue;
  const width = 1320;
  const height = 380;
  const paddingX = 150;
  const paddingTop = 84;
  const paddingBottom = 58;
  const plotHeight = height - paddingTop - paddingBottom;
  const plotWidth = width - paddingX * 2;
  const max = Math.max(...bars.map((bar) => bar.value), 0);
  const min = Math.min(...bars.map((bar) => bar.value), 0);
  const range = max - min || 1;
  const zeroY = paddingTop + (max / range) * plotHeight;
  const slot = plotWidth / bars.length;
  const barWidth = Math.min(slot * 0.5, 46);
  const dateLabelStep = Math.max(1, Math.ceil(bars.length / 8));
  const total = bars.reduce((sum, bar) => sum + bar.value, 0);

  const columns = bars.map((bar, index) => {
    const center = paddingX + slot * index + slot / 2;
    const size = Math.abs(bar.value) / range * plotHeight;
    // A period can net out to zero and still have moved real money in both
    // directions (a partner leaving while another joins). Those keep a
    // hairline bar so the breakdown stays hoverable instead of vanishing.
    const hasFlow = bar.inflow !== 0 || bar.outflow !== 0;
    const barHeight = bar.value === 0 ? (hasFlow ? 2 : 0) : Math.max(size, 2);

    return {
      ...bar,
      center,
      barHeight,
      hasFlow,
      x: center - barWidth / 2,
      y: bar.value >= 0 ? zeroY - barHeight : zeroY,
      labelY: bar.value >= 0 ? zeroY - barHeight - 12 : zeroY + barHeight + 24
    };
  });

  return (
    <div className="chart-panel">
      <div className="chart-header">
        <div>
          <h2>{title}</h2>
          {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        </div>
        <div className="chart-header-right">
          <span>{summaryLabel}: {formatValue(total)}</span>
          {controls}
        </div>
      </div>
      <div className="chart-scroll">
        <div className="chart-scroll-inner" style={{ '--chart-min-width': `${200 + bars.length * 78}px` }}>
          <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
            <line x1={paddingX} y1={zeroY} x2={width - paddingX} y2={zeroY} className="chart-axis" />
            <line x1={paddingX} y1={paddingTop} x2={paddingX} y2={height - paddingBottom} className="chart-axis" />
            {columns.map((column) => (
              <g key={`flow-${column.label}`}>
                <rect
                  x={column.x}
                  y={column.y}
                  width={barWidth}
                  height={column.barHeight}
                  rx="4"
                  fill={column.value < 0 ? negativeColor : positiveColor}
                >
                  <title>
                    {`${column.displayLabel}\n${inflowLabel}: ${formatValue(column.inflow)}\n${outflowLabel}: ${formatValue(column.outflow)}\n${summaryLabel}: ${formatValue(column.value)}`}
                  </title>
                </rect>
                <text
                  x={column.center}
                  y={column.labelY}
                  textAnchor="middle"
                  className={`chart-flow-value${column.value < 0 ? ' chart-flow-value-negative' : ''}`}
                >
                  {column.hasFlow ? displayValue(column.value) : '-'}
                </text>
              </g>
            ))}
            {columns.map((column, index) => (
              (index === 0 || index === columns.length - 1 || index % dateLabelStep === 0) && (
                <text
                  key={`flow-date-${column.label}`}
                  x={column.center}
                  y={height - paddingBottom + 34}
                  textAnchor="middle"
                  className="chart-date-label"
                >
                  {column.displayLabel}
                </text>
              )
            ))}
            <text x={paddingX + 4} y={paddingTop - 18} className="chart-value">{displayValue(max)}</text>
            {min < 0 && (
              <text x={paddingX + 4} y={height - paddingBottom - 10} className="chart-value">{displayValue(min)}</text>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
};

const SimpleBarChart = ({ title, data, valueKey, formatValue, emptyText, totalLabel, color = '#1b6b5f' }) => {
  const bars = (data || [])
    .map((entry) => ({
      label: entry.date,
      value: Number(entry[valueKey] || 0)
    }))
    .filter((entry) => entry.value !== 0);

  if (!bars.length) {
    return (
      <div className="chart-panel">
        <h2>{title}</h2>
        <p className="empty-chart">{emptyText}</p>
      </div>
    );
  }

  const max = Math.max(...bars.map((bar) => Math.max(bar.value, 0)), 0) || 1;

  return (
    <div className="chart-panel">
      <div className="chart-header">
        <h2>{title}</h2>
        <span>{formatValue(bars.reduce((sum, bar) => sum + bar.value, 0))} {totalLabel}</span>
      </div>
      <div className="bar-chart">
        {bars.map((bar) => (
          <div className="bar-row" key={bar.label}>
            <span>{bar.label}</span>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ width: bar.value > 0 ? `${Math.max((bar.value / max) * 100, 4)}%` : '0%', background: color }}
              />
            </div>
            <strong className={bar.value < 0 ? 'negative-value' : ''}>{formatValue(bar.value)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const compoundDefaults = {
  usd: {
    startingCapital: 5000,
    halfYearContribution: 2500,
    step: 500,
    formatValue: formatUsd,
    compactFormatValue: formatCompactUsd
  },
  czk: {
    startingCapital: 100000,
    halfYearContribution: 50000,
    step: 5000,
    formatValue: formatCzk,
    compactFormatValue: formatCompactCzk
  }
};

const CompoundInterestCalculator = ({ t, currency }) => {
  const currencyConfig = compoundDefaults[currency] || compoundDefaults.usd;
  const [startingCapital, setStartingCapital] = useState(currencyConfig.startingCapital);
  const [halfYearContribution, setHalfYearContribution] = useState(currencyConfig.halfYearContribution);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [years, setYears] = useState(30);

  useEffect(() => {
    const defaults = compoundDefaults[currency] || compoundDefaults.usd;

    setStartingCapital(defaults.startingCapital);
    setHalfYearContribution(defaults.halfYearContribution);
  }, [currency]);

  const normalizedYears = Math.min(Math.max(Number(years) || 1, 1), 40);
  const normalizedReturn = Math.min(Math.max(Number(expectedReturn) || 0, -50), 80);
  const start = Math.max(Number(startingCapital) || 0, 0);
  const semiannualContribution = Math.max(Number(halfYearContribution) || 0, 0);

  let baseValue = start;
  let optimisticValue = start;
  let pessimisticValue = start;
  let invested = start;
  const baseSemiannualReturn = Math.sqrt(1 + normalizedReturn / 100) - 1;
  const optimisticSemiannualReturn = Math.sqrt(1 + (normalizedReturn + 4) / 100) - 1;
  const pessimisticSemiannualReturn = Math.sqrt(1 + (normalizedReturn - 4) / 100) - 1;
  const projection = Array.from({ length: normalizedYears }, (_, index) => {
    const year = index + 1;
    Array.from({ length: 2 }).forEach(() => {
      invested += semiannualContribution;
      baseValue = (baseValue + semiannualContribution) * (1 + baseSemiannualReturn);
      optimisticValue = (optimisticValue + semiannualContribution) * (1 + optimisticSemiannualReturn);
      pessimisticValue = (pessimisticValue + semiannualContribution) * (1 + pessimisticSemiannualReturn);
    });

    return {
      year,
      invested,
      baseValue,
      optimisticValue,
      pessimisticValue
    };
  });

  const latest = projection[projection.length - 1] || {
    invested: start,
    baseValue: start,
    optimisticValue: start,
    pessimisticValue: start
  };
  const width = 900;
  const height = 300;
  const paddingX = 70;
  const paddingTop = 34;
  const paddingBottom = 52;
  const maxValue = Math.max(...projection.map((point) => point.optimisticValue), latest.invested, 1);
  const plotWidth = width - paddingX * 2;
  const plotHeight = height - paddingTop - paddingBottom;
  const toX = (index) => paddingX + (projection.length === 1 ? plotWidth : (index / (projection.length - 1)) * plotWidth);
  const toY = (value) => height - paddingBottom - (value / maxValue) * plotHeight;
  const basePath = projection.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toX(index)} ${toY(point.baseValue)}`).join(' ');
  const optimisticPath = projection.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toX(index)} ${toY(point.optimisticValue)}`).join(' ');
  const pessimisticPath = projection.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toX(index)} ${toY(point.pessimisticValue)}`).join(' ');
  const investedPath = projection.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toX(index)} ${toY(point.invested)}`).join(' ');
  const shouldShowCompoundYear = (point) => point.year === 1 || point.year % 5 === 0 || point.year === normalizedYears;
  const finalScenarioLabels = [
    { key: 'pessimistic', value: latest.pessimisticValue, offsetY: 18 },
    { key: 'optimistic', value: latest.optimisticValue, offsetY: -20 }
  ];

  return (
    <section className="members-section compound-section">
      <div className="tool-panel compound-panel">
        <div className="compound-header">
          <div>
            <h2>{t.compoundCalculator}</h2>
            <p>{t.compoundCalculatorHelp}</p>
          </div>
          <div className="compound-summary">
            <div>
              <span>{t.pessimisticScenario}</span>
              <strong>{currencyConfig.formatValue(latest.pessimisticValue)}</strong>
            </div>
            <div>
              <span>{t.baseScenario}</span>
              <strong>{currencyConfig.formatValue(latest.baseValue)}</strong>
            </div>
            <div>
              <span>{t.optimisticScenario}</span>
              <strong>{currencyConfig.formatValue(latest.optimisticValue)}</strong>
            </div>
          </div>
        </div>

        <div className="compound-layout">
          <div className="compound-controls">
            <label>
              <span>{t.startingCapital}</span>
              <input
                type="number"
                min="0"
                step={currencyConfig.step}
                value={startingCapital}
                onChange={(event) => setStartingCapital(event.target.value)}
              />
            </label>
            <label>
              <span>{t.halfYearContribution}</span>
              <input
                type="number"
                min="0"
                step={currencyConfig.step}
                value={halfYearContribution}
                onChange={(event) => setHalfYearContribution(event.target.value)}
              />
            </label>
            <label>
              <span>{t.expectedReturn}</span>
              <input
                type="number"
                min="-50"
                max="80"
                step="0.5"
                value={expectedReturn}
                onChange={(event) => setExpectedReturn(event.target.value)}
              />
            </label>
            <label>
              <span>{t.investmentYears}</span>
              <input
                type="number"
                min="1"
                max="40"
                step="1"
                value={years}
                onChange={(event) => setYears(event.target.value)}
              />
            </label>
          </div>

          <div className="compound-chart-wrap">
            <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t.compoundCalculator}>
              <line x1={paddingX} y1={height - paddingBottom} x2={width - paddingX} y2={height - paddingBottom} className="chart-axis" />
              <line x1={paddingX} y1={paddingTop} x2={paddingX} y2={height - paddingBottom} className="chart-axis" />
              {projection.map((point, index) => (
                shouldShowCompoundYear(point) && (
                  <g key={`compound-${point.year}`}>
                    <line x1={toX(index)} y1={paddingTop} x2={toX(index)} y2={height - paddingBottom} className="chart-guide" />
                    <text x={toX(index)} y={height - 18} textAnchor="middle" className="chart-date-label">{point.year}</text>
                  </g>
                )
              ))}
              <path d={investedPath} fill="none" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
              <path d={pessimisticPath} fill="none" stroke="#60a5fa" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              <path d={basePath} fill="none" stroke="#14b8a6" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              <path d={optimisticPath} fill="none" stroke="#38bdf8" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              {projection.map((point, index) => (
                (point.year % 5 === 0 || point.year === normalizedYears) && (
                  <g key={`compound-value-${point.year}`} transform={`translate(${toX(index)} ${toY(point.baseValue) - 18})`}>
                    <rect x="-46" y="-14" width="92" height="23" rx="5" className="chart-point-badge" />
                    <text y="2" textAnchor="middle" className="chart-point-value">
                      {currencyConfig.compactFormatValue(point.baseValue)}
                    </text>
                  </g>
                )
              ))}
              {projection.length > 0 && finalScenarioLabels.map((label) => (
                <g
                  key={`compound-final-${label.key}`}
                  transform={`translate(${toX(projection.length - 1)} ${toY(label.value) + label.offsetY})`}
                >
                  <rect x="-46" y="-14" width="92" height="23" rx="5" className="chart-point-badge" />
                  <text y="2" textAnchor="middle" className="chart-point-value">
                    {currencyConfig.compactFormatValue(label.value)}
                  </text>
                </g>
              ))}
              <text x={paddingX + 4} y={paddingTop - 12} className="chart-value">{currencyConfig.compactFormatValue(maxValue)}</text>
              <text x={paddingX + 4} y={height - paddingBottom - 8} className="chart-value">{currencyConfig.formatValue(0)}</text>
            </svg>
            <div className="compound-legend">
              <span><i className="legend-pessimistic" />{t.pessimisticScenario}</span>
              <span><i className="legend-net" />{t.baseScenario}</span>
              <span><i className="legend-optimistic" />{t.optimisticScenario}</span>
              <span><i className="legend-invested" />{t.totalInvested}</span>
            </div>
          </div>
        </div>

        <div className="compound-metrics">
          <div><span>{t.totalInvested}</span><strong>{currencyConfig.formatValue(latest.invested)}</strong></div>
          <div><span>{t.netGain}</span><strong>{currencyConfig.formatValue(Math.max(latest.baseValue - latest.invested, 0))}</strong></div>
        </div>
      </div>
    </section>
  );
};

export const DashboardPage = () => {
  const { logout, impersonation, startImpersonation, stopImpersonation, token } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [language, setLanguage] = useState(() => localStorage.getItem('fundLanguage') || 'cs');
  const [theme, setTheme] = useState(() => localStorage.getItem('fundTheme') || 'dark');
  const [partnerChartCurrency, setPartnerChartCurrency] = useState('usd');
  const [adminChartCurrency, setAdminChartCurrency] = useState('czk');
  const [maskAdminCapital, setMaskAdminCapital] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentFile, setDocumentFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreview, setExcelPreview] = useState(null);
  const [importPeriods, setImportPeriods] = useState([]);
  const [importArchive, setImportArchive] = useState([]);
  const [selectedImportPeriod, setSelectedImportPeriod] = useState('');
  const [selectedImportPeriods, setSelectedImportPeriods] = useState([]);
  const [manualImportPeriod, setManualImportPeriod] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [interimInfo, setInterimInfo] = useState(null);
  const [interimForm, setInterimForm] = useState({ returnPct: '', exchangeRate: '', date: todayIsoDate() });
  const [interimPreview, setInterimPreview] = useState(null);
  const [fundUpdates, setFundUpdates] = useState([]);
  const [fundUpdateTitle, setFundUpdateTitle] = useState('');
  const [fundUpdateMessage, setFundUpdateMessage] = useState('');
  const [fundUpdateTitleEn, setFundUpdateTitleEn] = useState('');
  const [fundUpdateMessageEn, setFundUpdateMessageEn] = useState('');
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [adminStatus, setAdminStatus] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [emergencyContact, setEmergencyContact] = useState(emptyEmergencyContact);
  const [emergencyContactMessage, setEmergencyContactMessage] = useState('');
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [newPartner, setNewPartner] = useState({ name: '', email: '' });
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const t = translations[language] || translations.cs;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const meResponse = await getMe();
        const me = meResponse.data;
        setUserData(me);
        setEmergencyContact({ ...emptyEmergencyContact(), ...(me.emergencyContact || {}) });
        setAllUsers([]);
        setAuditLogs([]);
        setImportPeriods([]);
        setImportArchive([]);

        if (me.role === 'admin') {
          const usersResponse = await getAllUsers();
          setAllUsers(usersResponse.data);
          const auditResponse = await getAuditLogs();
          setAuditLogs(auditResponse.data);
          const statusResponse = await getAdminStatus();
          setAdminStatus(statusResponse.data);
          const periodsResponse = await getImportPeriods();
          setImportPeriods(periodsResponse.data);
          const defaultPeriod = periodsResponse.data.find((period) => period.isDefault)?.date || periodsResponse.data.at(-1)?.date || '';
          setSelectedImportPeriod(defaultPeriod);
          setSelectedImportPeriods(defaultPeriod ? [defaultPeriod] : []);
          const archiveResponse = await getImportArchive();
          setImportArchive(archiveResponse.data);
          loadInterimInfo(await getInterimValuation());
        }

        const documentsResponse = await getDocuments();
        setDocuments(documentsResponse.data);
        const updatesResponse = await getFundUpdates();
        setFundUpdates(updatesResponse.data);
      } catch (err) {
        setError(err.response?.data?.error || translations.cs.failedLoad);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleLanguage = () => {
    const nextLanguage = language === 'cs' ? 'en' : 'cs';
    setLanguage(nextLanguage);
    localStorage.setItem('fundLanguage', nextLanguage);
  };

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'sunrise' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('fundTheme', nextTheme);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setError('');
    setSettingsMessage('');

    try {
      await changePassword(currentPassword, newPasswordValue);
      setCurrentPassword('');
      setNewPasswordValue('');
      setSettingsMessage(t.passwordUpdated);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleEmergencyContactChange = (field, value) => {
    setEmergencyContact((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleSaveEmergencyContact = async (event) => {
    event.preventDefault();
    setError('');
    setEmergencyContactMessage('');

    try {
      const response = await updateEmergencyContact(emergencyContact);
      setUserData(response.data);
      setEmergencyContact({ ...emptyEmergencyContact(), ...(response.data.emergencyContact || {}) });
      setEmergencyContactMessage(t.emergencyContactUpdated);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleUploadDocument = async (event) => {
    event.preventDefault();
    if (!documentFile) {
      return;
    }

    setError('');

    try {
      await uploadDocument(documentFile);
      setDocumentFile(null);
      event.target.reset();
      const documentsResponse = await getDocuments();
      setDocuments(documentsResponse.data);
      if (isAdmin) {
        const auditResponse = await getAuditLogs();
        setAuditLogs(auditResponse.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleUploadExcel = async (event) => {
    event.preventDefault();
    if (!excelFile) {
      return;
    }

    setError('');
    setSettingsMessage('');

    try {
      const normalizedStatementDate = normalizeAmericanDate(statementDate);
      const response = await uploadExcel(
        excelFile,
        getSelectedLastImportPeriod(),
        normalizedStatementDate || undefined
      );
      setExcelFile(null);
      event.target.reset();
      await refreshAfterDataChange();
      setExcelPreview(null);
      setSettingsMessage(`Excel import hotov: ${response.data.result.importedPartners} partnerů, poslední období ${response.data.result.lastDate}`);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  // Fund values changed (Excel import or interim update): reload everything
  // that shows them.
  const refreshAfterDataChange = async () => {
    const [meResponse, usersResponse, auditResponse, statusResponse, archiveResponse, interimResponse] = await Promise.all([
      getMe(),
      getAllUsers(),
      getAuditLogs(),
      getAdminStatus(),
      getImportArchive(),
      getInterimValuation()
    ]);
    setUserData(meResponse.data);
    setAllUsers(usersResponse.data);
    setAuditLogs(auditResponse.data);
    setAdminStatus(statusResponse.data);
    setImportArchive(archiveResponse.data);
    loadInterimInfo(interimResponse);
  };

  // Prefills the form with the active update, or the statement's rate.
  const loadInterimInfo = (response) => {
    const { basis, current } = response.data;
    setInterimInfo(response.data);
    setInterimForm((form) => ({
      ...form,
      returnPct: current ? String(current.returnPct) : '',
      exchangeRate: String(current?.exchangeRate || basis?.exchangeRate || '')
    }));
  };

  const handleInterimFieldChange = (field, value) => {
    setInterimPreview(null);
    setInterimForm((form) => ({ ...form, [field]: value }));
  };

  const handlePreviewInterim = async () => {
    setError('');

    try {
      const response = await previewInterimValuation(interimForm);
      setInterimPreview({ ...response.data, applied: false });
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleApplyInterim = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const response = await applyInterimValuation(interimForm);
      await refreshAfterDataChange();
      setInterimPreview({ ...response.data, applied: true });
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleRevertInterim = async () => {
    if (!window.confirm(t.interimConfirmRevert)) {
      return;
    }

    setError('');

    try {
      await revertInterimValuation();
      setInterimPreview(null);
      await refreshAfterDataChange();
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const getSelectedLastImportPeriod = () => {
    if (manualImportPeriod.trim()) {
      return manualImportPeriod.trim();
    }

    const selected = selectedImportPeriods.length > 0 ? selectedImportPeriods : [selectedImportPeriod].filter(Boolean);
    const selectedSet = new Set(selected);
    const selectedOrdered = importPeriods.filter((period) => selectedSet.has(period.date));
    return selectedOrdered.at(-1)?.date || selectedImportPeriod;
  };

  const handlePreviewExcel = async () => {
    if (!excelFile) {
      return;
    }

    setError('');
    setSettingsMessage('');

    try {
      const normalizedStatementDate = normalizeAmericanDate(statementDate);
      const response = await previewExcel(
        excelFile,
        manualImportPeriod.trim() || undefined,
        normalizedStatementDate || undefined
      );
      setExcelPreview(response.data.preview);
      const detected = response.data.preview.detectedPeriods || response.data.preview.availablePeriods || [];
      if (detected.length > 0) {
        setImportPeriods(detected);
        setSelectedImportPeriod(response.data.preview.selectedLastDate || detected.at(-1).date);
        setSelectedImportPeriods([response.data.preview.selectedLastDate || detected.at(-1).date]);
        setManualImportPeriod('');
      }
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleSetupTwoFactor = async () => {
    setError('');

    try {
      const response = await setupTwoFactor();
      setTwoFactorSetup(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleEnableTwoFactor = async () => {
    setError('');

    try {
      await enableTwoFactor(twoFactorCode);
      const meResponse = await getMe();
      setUserData(meResponse.data);
      setTwoFactorCode('');
      setSettingsMessage('2FA zapnuto');
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleExportCsv = async () => {
    setError('');

    try {
      const response = await exportPartnersCsv();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'partners-export.csv');
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      if (isAdmin) {
        const auditResponse = await getAuditLogs();
        setAuditLogs(auditResponse.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleExportMyResults = async (format = 'csv') => {
    setError('');

    try {
      const isExcel = format === 'xlsx';
      const response = isExcel ? await exportMyResultsExcel() : await exportMyResultsCsv();
      const type = isExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type }));
      const link = window.document.createElement('a');
      const safeName = String(userData?.name || 'partner')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase() || 'partner';
      link.href = url;
      link.setAttribute('download', `${safeName}-vysledky.${isExcel ? 'xlsx' : 'csv'}`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleDownloadBackup = async () => {
    setError('');

    try {
      const response = await downloadBackup();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/json' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', `portal-backup-${new Date().toISOString().slice(0, 10)}.json`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const statusResponse = await getAdminStatus();
      setAdminStatus(statusResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleViewAsPartner = async (partnerId) => {
    setError('');

    try {
      const response = await impersonateUser(partnerId);
      startImpersonation(response.data);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/dashboard');
  };

  const handleAdminResetPassword = async (partner) => {
    if (!window.confirm(`${t.confirmPasswordReset}\n\n${partner.name} (${partner.email})`)) {
      return;
    }

    setError('');
    setPasswordResetResult(null);

    try {
      const response = await adminResetPassword(partner._id);
      setPasswordResetResult({
        partner,
        temporaryPassword: response.data.temporaryPassword
      });
      const auditResponse = await getAuditLogs();
      setAuditLogs(auditResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const refreshAdminUsers = async () => {
    const [usersResponse, statusResponse, auditResponse] = await Promise.all([
      getAllUsers(),
      getAdminStatus(),
      getAuditLogs()
    ]);

    setAllUsers(usersResponse.data);
    setAdminStatus(statusResponse.data);
    setAuditLogs(auditResponse.data);
  };

  const handleCreatePartner = async (event) => {
    event.preventDefault();
    setError('');
    setPasswordResetResult(null);

    try {
      const response = await createPartner(newPartner.name, newPartner.email);
      setNewPartner({ name: '', email: '' });
      setPasswordResetResult({
        partner: response.data.user,
        temporaryPassword: response.data.temporaryPassword
      });
      setSettingsMessage(t.partnerCreated);
      await refreshAdminUsers();
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleDeletePartner = async (partner) => {
    if (!window.confirm(`${t.confirmDeletePartner}\n\n${partner.name} (${partner.email})`)) {
      return;
    }

    setError('');

    try {
      await deletePartner(partner._id);
      if (selectedPartnerId === partner._id) {
        setSelectedPartnerId('');
      }
      await refreshAdminUsers();
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleDeleteResetRequest = async (requestId) => {
    setError('');

    try {
      await deleteResetRequest(requestId);
      const [statusResponse, auditResponse] = await Promise.all([
        getAdminStatus(),
        getAuditLogs()
      ]);
      setAdminStatus(statusResponse.data);
      setAuditLogs(auditResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    setError('');

    try {
      await deleteDocument(documentId);
      setDocuments(documents.filter((document) => document.id !== documentId));
      if (isAdmin) {
        const auditResponse = await getAuditLogs();
        setAuditLogs(auditResponse.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleDownloadDocument = async (document) => {
    setError('');

    try {
      const response = await downloadDocument(document.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleCreateFundUpdate = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const response = await createFundUpdate(fundUpdateTitle, fundUpdateMessage, fundUpdateTitleEn, fundUpdateMessageEn);
      setFundUpdateTitle('');
      setFundUpdateMessage('');
      setFundUpdateTitleEn('');
      setFundUpdateMessageEn('');
      setFundUpdates([response.data, ...fundUpdates]);
      if (isAdmin) {
        const auditResponse = await getAuditLogs();
        setAuditLogs(auditResponse.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleEditUpdateChange = (field, value) => {
    setEditingUpdate((current) => ({ ...current, [field]: value }));
  };

  const handleSaveUpdateEdit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const response = await updateFundUpdate(editingUpdate.id, {
        title: editingUpdate.title,
        message: editingUpdate.message,
        titleEn: editingUpdate.titleEn,
        messageEn: editingUpdate.messageEn
      });
      setFundUpdates(fundUpdates.map((update) => (update.id === editingUpdate.id ? response.data : update)));
      setEditingUpdate(null);
      const auditResponse = await getAuditLogs();
      setAuditLogs(auditResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const handleDeleteFundUpdate = async (updateId) => {
    setError('');

    try {
      await deleteFundUpdate(updateId);
      setFundUpdates(fundUpdates.filter((update) => update.id !== updateId));
      const auditResponse = await getAuditLogs();
      setAuditLogs(auditResponse.data);
    } catch (err) {
      setError(err.response?.data?.error || t.failedUpdate);
    }
  };

  const demoAccount = allUsers.find((user) => user.role === 'demo') || null;
  const partners = allUsers
    .filter((user) => user.role !== 'admin' && user.role !== 'demo')
    .sort((userA, userB) => (userB.currentShare || 0) - (userA.currentShare || 0));
  const isAdmin = userData?.role === 'admin';
  const selectedPartner = partners.find((partner) => partner._id === selectedPartnerId);
  const documentDownloadLogs = auditLogs.filter((log) => log.action === 'document_downloaded');
  const totalCapital = partners.reduce((sum, user) => sum + (user.capital || 0), 0);
  const totalContributed = partners.reduce((sum, user) => sum + sumDeposits(user.deposits || [], 'amountCzk'), 0);
  const fundHistory = partners.reduce((byDate, partner) => {
    (partner.history || []).forEach((entry) => {
      if (!byDate[entry.date]) {
        byDate[entry.date] = { date: entry.date, capitalUsd: 0, capitalCzk: 0 };
      }

      byDate[entry.date].capitalUsd += entry.capitalUsd || 0;
      byDate[entry.date].capitalCzk += entry.capitalCzk || 0;
    });

    return byDate;
  }, {});
  const fundHistoryList = Object.values(fundHistory)
    .sort((entryA, entryB) => (parseDate(entryA.date) || 0) - (parseDate(entryB.date) || 0));
  const latestFundHistory = [...fundHistoryList].reverse().find((entry) => entry.capitalCzk > 0 || entry.capitalUsd > 0);
  const latestPartnerFundTotals = [...(userData?.fundHistory || [])]
    .reverse()
    .find((entry) => entry.capitalCzk > 0 || entry.capitalUsd > 0);
  const partnerDisplayHistory = applyStatementDateToLatestHistory(userData?.history || [], userData?.statementDate);
  const latestPartnerHistory = [...(userData?.history || [])].reverse()[0];
  const totalDepositUsd = sumDeposits(userData?.deposits || [], 'amountUsd');
  const totalDepositCzk = sumDeposits(userData?.deposits || [], 'amountCzk');
  const fundFlows = fundFlowsByPeriod(partners);
  const adminFlowConfig = adminChartCurrency === 'usd'
    ? { valueKey: 'netUsd', inflowKey: 'inflowUsd', outflowKey: 'outflowUsd', formatValue: formatUsd, compactFormatValue: formatCompactUsd }
    : { valueKey: 'netCzk', inflowKey: 'inflowCzk', outflowKey: 'outflowCzk', formatValue: formatCzk, compactFormatValue: formatCompactCzk };
  const adminChartConfig = adminChartCurrency === 'usd'
    ? {
      title: t.fundCapitalGrowth,
      valueKey: 'capitalUsd',
      formatValue: formatUsd,
      compactFormatValue: formatCompactUsd,
      latestValue: latestFundHistory ? formatUsd(latestFundHistory.capitalUsd) : '-'
    }
    : {
      title: t.fundCzkGrowth,
      valueKey: 'capitalCzk',
      formatValue: formatCzk,
      compactFormatValue: formatCompactCzk,
      latestValue: latestFundHistory ? formatCzk(latestFundHistory.capitalCzk) : '-'
    };
  const partnerChartConfig = partnerChartCurrency === 'usd'
    ? {
      valueKey: 'capitalUsd',
      contributionValueKey: 'amountUsd',
      formatValue: formatUsd,
      compactFormatValue: formatCompactUsd,
      capital: userData?.capitalUsd || 0,
      totalContributed: totalDepositUsd,
      netGain: (userData?.capitalUsd || 0) - totalDepositUsd,
      latestValue: latestPartnerHistory ? formatUsd(latestPartnerHistory.capitalUsd) : '-'
    }
    : {
      valueKey: 'capitalCzk',
      contributionValueKey: 'amountCzk',
      formatValue: formatCzk,
      compactFormatValue: formatCompactCzk,
      capital: userData?.capital || 0,
      totalContributed: totalDepositCzk,
      netGain: (userData?.capital || 0) - totalDepositCzk,
      latestValue: latestPartnerHistory ? formatCzk(latestPartnerHistory.capitalCzk) : '-'
    };
  const hundredInvestedGrowthRaw = userData?.fundPerformance || buildHundredInvestedGrowth(userData?.fundHistory || [], 'capitalCzk');
  const hundredInvestedGrowth = userData?.statementDate && hundredInvestedGrowthRaw.length
    ? hundredInvestedGrowthRaw.map((entry, index) => (
      index === hundredInvestedGrowthRaw.length - 1
        ? { ...entry, displayDate: normalizeAmericanDate(userData.statementDate) }
        : entry
    ))
    : hundredInvestedGrowthRaw;
  const hundredInvestedIrr = calculateAnnualizedReturn(hundredInvestedGrowth);
  const latestHundredInvested = hundredInvestedGrowth[hundredInvestedGrowth.length - 1];
  const visiblePartnerFees = filterFeesThroughLatestHistory(userData?.fees || [], userData?.history || []);
  const partnerFeeSummary = {
    totalUsd: Number(visiblePartnerFees.reduce((sum, fee) => sum + (fee.amountUsd || 0), 0).toFixed(2)),
    totalCzk: Number(visiblePartnerFees.reduce((sum, fee) => sum + (fee.amountCzk || 0), 0).toFixed(2)),
    yearly: visiblePartnerFees.map((fee) => ({
      year: fee.period || fee.year || fee.date,
      amountUsd: fee.amountUsd || 0,
      amountCzk: fee.amountCzk || 0
    }))
  };
  const feeData = isAdmin ? userData?.fundFees : partnerFeeSummary;
  const maskValue = (value, formatter) => (isAdmin && maskAdminCapital ? '••••••' : formatter(value));
  const maskPercent = (value) => (isAdmin && maskAdminCapital ? '••••••' : formatPercent(value));
  const adminChartFormatValue = (value) => maskValue(value, adminChartConfig.formatValue);
  const currencyToggle = (
    <div className="currency-toggle" aria-label="Currency">
      <button
        type="button"
        className={adminChartCurrency === 'usd' ? 'active' : ''}
        onClick={() => setAdminChartCurrency('usd')}
      >
        USD
      </button>
      <button
        type="button"
        className={adminChartCurrency === 'czk' ? 'active' : ''}
        onClick={() => setAdminChartCurrency('czk')}
      >
        CZK
      </button>
    </div>
  );
  const adminChartCompactFormatValue = (value) => maskValue(value, adminChartConfig.compactFormatValue);
  const adminChartLatestValue = maskAdminCapital ? '••••••' : adminChartConfig.latestValue;

  if (loading) {
    return <div className={`dashboard-container theme-${theme}`}><p className="loading-text">{t.loading}</p></div>;
  }

  return (
    <div className={`dashboard-container theme-${theme}`}>
      <header className="dashboard-header">
        <div>
          <h1>{isAdmin ? t.adminDashboard : t.partnerDashboard}</h1>
          <p>{isAdmin ? t.fullOverview : t.partnerAccount}</p>
        </div>
        <div className="header-right">
          <span className="user-name">{userData?.name}</span>
          {isAdmin && (
            <button
              type="button"
              className="mask-toggle-btn"
              onClick={() => setMaskAdminCapital((masked) => !masked)}
            >
              {maskAdminCapital ? t.showCapital : t.hideCapital}
            </button>
          )}
          <button onClick={toggleLanguage} className="language-btn" aria-label={t.language}>
            {language === 'cs' ? 'CZ' : 'EN'}
            <span>{t.switchToEnglish}</span>
          </button>
          <button onClick={toggleTheme} className="language-btn theme-toggle-btn" aria-label={t.theme}>
            {theme === 'dark' ? '☀' : '☾'}
            <span>{theme === 'dark' ? t.lightTheme : t.darkTheme}</span>
          </button>
          <button onClick={handleLogout} className="logout-btn">{t.logout}</button>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}
      {impersonation && (
        <section className="members-section notification-strip">
          <strong>{t.viewingAsPartner}</strong>
          <span>{userData?.name} · {userData?.email}</span>
          <button type="button" className="download-link" onClick={handleStopImpersonation}>
            {t.stopViewingAsPartner}
          </button>
        </section>
      )}
      {isAdmin ? (
        <>
          <section className="dashboard-content">
            <div className="metric-panel">
              <span>{t.totalFundValue}</span>
              <strong>{maskValue(totalCapital, formatCzk)}</strong>
            </div>
            <div className="metric-panel">
              <span>{t.totalContributions}</span>
              <strong>{maskValue(totalContributed, formatCzk)}</strong>
            </div>
            <div className="metric-panel">
              <span>{t.partners}</span>
              <strong>{partners.length}</strong>
            </div>
          </section>

          <section className="members-section status-grid">
            <div className="tool-panel">
              <h2>{t.adminStatus}</h2>
              <div className="status-list">
                <div><span>{t.storage}</span><strong>{formatStorageUsage(adminStatus?.storageUsage, adminStatus?.storage)}</strong></div>
                <div><span>{t.partners}</span><strong>{adminStatus?.partnerCount ?? partners.length}</strong></div>
                <div><span>{t.latestImport}</span><strong>{normalizeAmericanDate(adminStatus?.latestImport?.statementDate || adminStatus?.latestImport?.lastDate || '-')}</strong></div>
                <div><span>{t.latestBackup}</span><strong>{adminStatus?.latestBackup ? formatDateTime(adminStatus.latestBackup.createdAt, language) : '-'}</strong></div>
              </div>
            </div>
            <div className="tool-panel">
              <h2>{t.resetRequests}</h2>
              {(adminStatus?.resetRequests || []).length === 0 ? (
                <p className="empty-chart">{t.noAudit}</p>
              ) : (
                <div className="mini-log-list">
                  {adminStatus.resetRequests.slice(0, 5).map((request) => (
                    <div className="mini-log-row request-row" key={request.id}>
                      <div>
                        <strong>{request.userName}</strong>
                        <span>{request.userEmail} · {formatDateTime(request.createdAt, language)}</span>
                      </div>
                      <button
                        type="button"
                        className="download-link secondary-action"
                        onClick={() => handleDeleteResetRequest(request.id)}
                      >
                        {t.delete}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="members-section chart-grid admin-chart-grid">
            <SimpleLineChart
              title={adminChartConfig.title}
              data={fundHistoryList}
              valueKey={adminChartConfig.valueKey}
              formatValue={adminChartFormatValue}
              compactFormatValue={adminChartCompactFormatValue}
              summary={`${t.latest}: ${adminChartLatestValue}`}
              emptyText={t.notEnoughHistory}
              color="#60a5fa"
              controls={currencyToggle}
            />
            <NetFlowChart
              title={t.fundNetFlows}
              subtitle={t.fundNetFlowsHelp}
              data={fundFlows}
              valueKey={adminFlowConfig.valueKey}
              inflowKey={adminFlowConfig.inflowKey}
              outflowKey={adminFlowConfig.outflowKey}
              formatValue={(value) => maskValue(value, adminFlowConfig.formatValue)}
              compactFormatValue={(value) => maskValue(value, adminFlowConfig.compactFormatValue)}
              emptyText={t.noNetFlows}
              summaryLabel={t.netFlowTotal}
              inflowLabel={t.inflow}
              outflowLabel={t.outflow}
              controls={currencyToggle}
            />
          </section>

          <section className="members-section">
            <h2>{t.partnerAccounts}</h2>
            <div className="members-table">
              <table>
                <thead>
                  <tr>
                    <th>{t.name}</th>
                    <th>{t.email}</th>
                    <th>{t.capitalUsd}</th>
                    <th>{t.capital}</th>
                    <th>{t.contributed}</th>
                    <th>{t.share}</th>
                    <th>{t.lastSeen}</th>
                    <th>{t.activityLastHalfYear}</th>
                    <th>{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((member) => (
                    <tr key={member._id}>
                      <td>{member.name}</td>
                      <td>{member.email}</td>
                      <td>{maskValue(member.capitalUsd, formatUsd)}</td>
                      <td>{maskValue(member.capital, formatCzk)}</td>
                      <td>{maskValue(sumDeposits(member.deposits || [], 'amountCzk'), formatCzk)}</td>
                      <td>{maskPercent(member.currentShare)}</td>
                      <td>{formatDateTime(latestDateValue(member.lastRefreshAt, member.lastLoginAt), language)}</td>
                      <td>{member.activityCountLastHalfYear || 0}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => setSelectedPartnerId(selectedPartnerId === member._id ? '' : member._id)}
                            className="edit-btn"
                          >
                            {selectedPartnerId === member._id ? t.closeDetail : t.detail}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleViewAsPartner(member._id)}
                            className="download-link"
                          >
                            {t.viewAsPartner}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAdminResetPassword(member)}
                            className="cancel-btn"
                          >
                            {t.resetPassword}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePartner(member)}
                            className="danger-btn"
                          >
                            {t.delete}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {passwordResetResult && (
              <div className="partner-detail-panel">
                <div className="chart-header">
                  <div>
                    <h2>{t.temporaryPassword}: {passwordResetResult.partner.name}</h2>
                    <p>{t.temporaryPasswordHelp}</p>
                  </div>
                  <strong>{passwordResetResult.temporaryPassword}</strong>
                </div>
                <button
                  type="button"
                  className="download-link"
                  onClick={() => setPasswordResetResult(null)}
                >
                  {t.closeDetail}
                </button>
              </div>
            )}
            {selectedPartner && (
              <div className="partner-detail-panel">
                <div className="chart-header">
                  <div>
                    <h2>{t.partnerDetail}: {selectedPartner.name}</h2>
                    <p>{selectedPartner.email}</p>
                  </div>
                  <strong>{maskValue(selectedPartner.capital, formatCzk)} / {maskValue(selectedPartner.capitalUsd, formatUsd)}</strong>
                </div>
                <div className="login-history-panel">
                  <h3>{t.loginHistory}</h3>
                  <div className="status-list compact-status-list">
                    <div><span>{t.activityLastHalfYear}</span><strong>{selectedPartner.activityCountLastHalfYear || 0}</strong></div>
                    <div><span>{t.lastLogin}</span><strong>{formatDateTime(selectedPartner.lastLoginAt, language)}</strong></div>
                    <div><span>{t.lastRefresh}</span><strong>{formatDateTime(selectedPartner.lastRefreshAt, language)}</strong></div>
                  </div>
                  {(selectedPartner.loginHistory || []).length === 0 ? (
                    <p className="empty-chart">{t.noLoginHistory}</p>
                  ) : (
                    <div className="mini-log-list login-history-list">
                      {selectedPartner.loginHistory.map((login) => (
                        <div className="mini-log-row" key={login.id || login.createdAt}>
                          <strong>{formatDateTime(login.createdAt, language)}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="emergency-contact-readonly">
                  <h3>{t.emergencyContact}</h3>
                  {hasEmergencyContact(selectedPartner.emergencyContact) ? (
                    <div className="emergency-contact-grid">
                      <div><span>{t.emergencyContactName}</span><strong>{selectedPartner.emergencyContact?.name || '-'}</strong></div>
                      <div><span>{t.emergencyContactRelationship}</span><strong>{selectedPartner.emergencyContact?.relationship || '-'}</strong></div>
                      <div><span>{t.emergencyContactEmail}</span><strong>{selectedPartner.emergencyContact?.email || '-'}</strong></div>
                      <div><span>{t.emergencyContactPhone}</span><strong>{selectedPartner.emergencyContact?.phone || '-'}</strong></div>
                      <div className="emergency-contact-note"><span>{t.emergencyContactNote}</span><strong>{selectedPartner.emergencyContact?.note || '-'}</strong></div>
                    </div>
                  ) : (
                    <p className="empty-chart">{t.noEmergencyContact}</p>
                  )}
                </div>
                <SimpleLineChart
                  title={t.capitalGrowth}
                  data={applyStatementDateToLatestHistory(selectedPartner.history || [], selectedPartner.statementDate)}
                  valueKey="capitalCzk"
                  formatValue={(value) => maskValue(value, formatCzk)}
                  compactFormatValue={(value) => maskValue(value, formatCompactCzk)}
                  summary={`${t.latest}: ${maskValue(selectedPartner.capital, formatCzk)}`}
                  emptyText={t.notEnoughHistory}
                  color="#14b8a6"
                  showPointLabels={false}
                />
                <div className="members-table">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.date}</th>
                        <th>{t.amountUsd}</th>
                        <th>{t.amountCzk}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedPartner.deposits || []).map((deposit, index) => (
                        <tr key={`${deposit.date}-${index}`}>
                          <td>{deposit.date}</td>
                          <td className={deposit.amountUsd < 0 ? 'negative-value' : ''}>{maskValue(deposit.amountUsd, formatUsd)}</td>
                          <td className={deposit.amountCzk < 0 ? 'negative-value' : ''}>{maskValue(deposit.amountCzk, formatCzk)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <section className="dashboard-content partner-metrics-grid">
            <div className="metric-panel primary">
              <span>{t.currentCapital}</span>
              <strong>{partnerChartConfig.formatValue(partnerChartConfig.capital)}</strong>
              <small>{partnerChartCurrency === 'usd' ? formatCzk(userData?.capital) : formatUsd(userData?.capitalUsd)}</small>
            </div>
            <div className="metric-panel">
              <span>{t.totalContributed}</span>
              <strong>{partnerChartConfig.formatValue(partnerChartConfig.totalContributed)}</strong>
            </div>
            <div className="metric-panel">
              <span>{t.netGain}</span>
              <strong>{partnerChartConfig.formatValue(partnerChartConfig.netGain)}</strong>
            </div>
            <div className="metric-panel">
              <span>{t.fundTotalCapital}</span>
              <strong>
                {partnerChartCurrency === 'usd'
                  ? formatUsd(latestPartnerFundTotals?.capitalUsd)
                  : formatCzk(latestPartnerFundTotals?.capitalCzk)}
              </strong>
              <small>{t.yourFundShare}: {formatPercent(userData?.currentShare)}</small>
            </div>
          </section>

          <section className="members-section partner-chart-layout">
            <SimpleLineChart
              title={t.yourNetWorthGrowth}
              data={partnerDisplayHistory}
              valueKey={partnerChartConfig.valueKey}
              formatValue={partnerChartConfig.formatValue}
              compactFormatValue={partnerChartConfig.compactFormatValue}
              summary={`${t.latest}: ${partnerChartConfig.latestValue}`}
              emptyText={t.notEnoughHistory}
              contributionData={contributionsByHistoryPeriod(userData?.history || [], userData?.deposits || [])}
              contributionTitle={t.periodContributions}
              contributionValueKey={partnerChartConfig.contributionValueKey}
              formatContribution={partnerChartConfig.formatValue}
              controls={(
                <div className="currency-toggle" aria-label="Currency">
                  <button
                    type="button"
                    className={partnerChartCurrency === 'usd' ? 'active' : ''}
                    onClick={() => setPartnerChartCurrency('usd')}
                  >
                    USD
                  </button>
                  <button
                    type="button"
                    className={partnerChartCurrency === 'czk' ? 'active' : ''}
                    onClick={() => setPartnerChartCurrency('czk')}
                  >
                    CZK
                  </button>
                </div>
              )}
              color="#14b8a6"
            />
            <SimpleBarChart
              title={t.yourContributionsByYear}
              data={yearlyContributions(userData?.deposits || [])}
              valueKey={partnerChartConfig.contributionValueKey}
              formatValue={partnerChartConfig.formatValue}
              emptyText={t.noContributions}
              totalLabel={t.total}
              color="#60a5fa"
            />
          </section>

          <section className="members-section split-section compact-tables">
            <div>
              <h2>{t.yourContributions}</h2>
              <div className="members-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t.date}</th>
                      <th>{t.amountUsd}</th>
                      <th>{t.amountCzk}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(userData?.deposits || []).map((deposit, index) => (
                      <tr key={`${deposit.date}-${index}`}>
                        <td>{deposit.date}</td>
                        <td>{formatUsd(deposit.amountUsd)}</td>
                        <td>{formatCzk(deposit.amountCzk)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2>{t.capitalGrowth}</h2>
              <div className="members-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t.date}</th>
                      <th>{t.capitalUsd}</th>
                      <th>{t.capital}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerDisplayHistory.map((entry, index) => (
                      <tr key={`${entry.date}-${index}`}>
                        <td>{entry.displayDate || entry.date}</td>
                        <td>{formatUsd(entry.capitalUsd)}</td>
                        <td>{entry.capitalCzk ? formatCzk(entry.capitalCzk) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="members-section bottom-chart-section">
            <SimpleLineChart
              title={t.hundredInvestedGrowth}
              data={hundredInvestedGrowth}
              valueKey="indexValue"
              formatValue={formatIndexValue}
              compactFormatValue={formatIndexValue}
              summary={`${t.irrFromStart}: ${hundredInvestedIrr === null ? '-' : formatPercent(hundredInvestedIrr)}${
                latestHundredInvested ? ` | ${t.latest}: ${formatIndexValue(latestHundredInvested.indexValue)}` : ''
              }`}
              emptyText={t.notEnoughHistory}
              color="#a78bfa"
            />
          </section>

          <section className="members-section partner-export-section">
            <div className="tool-panel partner-export-panel">
              <div>
                <h2>{t.exportMyResults}</h2>
                <p>{t.partnerExportHelp}</p>
              </div>
              <div className="partner-export-actions">
                <button type="button" className="download-link" onClick={() => handleExportMyResults('xlsx')}>
                  {t.exportMyResultsExcel}
                </button>
                <button type="button" className="download-link secondary-action" onClick={() => handleExportMyResults('csv')}>
                  {t.exportMyResultsCsv}
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      <section className={`members-section tools-section${isAdmin ? '' : ' partner-tools-section'}`}>
        <div className="tool-panel fee-panel">
          <h2>{isAdmin ? t.fundFees : t.partnerFees}</h2>
          <div className="fee-total">
            <span>{t.totalFees}</span>
            <strong>
              {partnerChartCurrency === 'usd'
                ? formatUsd(feeData?.totalUsd)
                : formatCzk(feeData?.totalCzk)}
            </strong>
          </div>
          <div className="fee-list">
            <span>{t.yearlyFees}</span>
            {(feeData?.yearly || []).map((fee) => (
              <div className="fee-row" key={fee.year}>
                <strong>{fee.year}</strong>
                <span>{partnerChartCurrency === 'usd' ? formatUsd(fee.amountUsd) : formatCzk(fee.amountCzk)}</span>
              </div>
            ))}
          </div>
          {!isAdmin && userData?.pendingFee && (
            <div className="fee-list">
              <span>{t.pendingFee}</span>
              <div className="fee-row">
                <strong>{userData.pendingFee.fundYear}</strong>
                <span>
                  {partnerChartCurrency === 'usd'
                    ? formatUsd(userData.pendingFee.amountUsd)
                    : formatCzk(userData.pendingFee.amountCzk)}
                </span>
              </div>
              <small className="field-hint">{t.pendingFeeHint}</small>
            </div>
          )}
        </div>

        <div className="tool-panel settings-panel">
          <h2>{t.accountSettings}</h2>
          <form className="settings-form" onSubmit={handleChangePassword}>
            <div className="settings-password-field">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                placeholder={t.currentPassword}
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((visible) => !visible)}
              >
                {showCurrentPassword ? t.hidePassword : t.showPassword}
              </button>
            </div>
            <div className="settings-password-field">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPasswordValue}
                onChange={(event) => setNewPasswordValue(event.target.value)}
                placeholder={t.newPassword}
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((visible) => !visible)}
              >
                {showNewPassword ? t.hidePassword : t.showPassword}
              </button>
            </div>
            <button type="submit" className="save-btn">{t.changePassword}</button>
          </form>
          {settingsMessage && <p className="success-text">{settingsMessage}</p>}
          {isAdmin && (
            <div className="two-factor-box">
              <h3>{t.twoFactor}</h3>
              <button type="button" className="download-link" onClick={handleSetupTwoFactor}>{t.setup2fa}</button>
              {twoFactorSetup?.qrCode && (
                <>
                  <img src={twoFactorSetup.qrCode} alt="2FA QR" />
                  <input
                    value={twoFactorCode}
                    onChange={(event) => setTwoFactorCode(event.target.value)}
                    placeholder={t.twoFactorCode}
                  />
                  <button type="button" className="save-btn" onClick={handleEnableTwoFactor}>{t.enable2fa}</button>
                </>
              )}
            </div>
          )}
        </div>

        {!isAdmin && (
          <div className="tool-panel fund-contact-panel">
            <h2>{t.fundContacts}</h2>
            <div className="fund-contact-block">
              <strong>{t.fundContactManager}</strong>
              <p>{t.fundContactManagerHelp}</p>
              <div className="fund-contact-links">
                <a href="tel:+420608810112">+420 608 810 112</a>
                <a href="mailto:vojta@naar.cz">vojta@naar.cz</a>
              </div>
            </div>
            <div className="fund-contact-block">
              <strong>{t.fundContactContinuity}</strong>
              <p>{t.fundContactContinuityHelp}</p>
              <div className="fund-contact-links">
                <a href="tel:+420773757664">+420 773 757 664</a>
                <a href="mailto:jana.naarova@gmail.com">jana.naarova@gmail.com</a>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="tool-panel admin-tools-panel">
            <h2>{t.adminTools}</h2>
            <form className="upload-form" onSubmit={handleCreatePartner}>
              <strong>{t.createPartner}</strong>
              <p className="tool-help-text">{t.createPartnerHelp}</p>
              <input
                type="text"
                value={newPartner.name}
                onChange={(event) => setNewPartner((current) => ({ ...current, name: event.target.value }))}
                placeholder={t.name}
                required
              />
              <input
                type="email"
                value={newPartner.email}
                onChange={(event) => setNewPartner((current) => ({ ...current, email: event.target.value }))}
                placeholder={t.email}
                required
              />
              <button type="submit" className="save-btn">{t.createPartner}</button>
            </form>
            {demoAccount && (
              <div className="import-preview">
                <strong>{t.demoAccountTitle}</strong>
                <p className="tool-help-text">{t.demoAccountHelp}</p>
                <span>{t.email}: {demoAccount.email}</span>
                <span>{t.demoAccountPassword}: Ukazka-2026</span>
              </div>
            )}
            <form className="upload-form" onSubmit={handleUploadExcel}>
              <strong>{t.importExcel}</strong>
              <input
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={(event) => setExcelFile(event.target.files?.[0] || null)}
              />
              <label className="field-label">
                {t.lastImportPeriod}
                <select
                  value={selectedImportPeriod}
                  onChange={(event) => {
                    setExcelPreview(null);
                    setSelectedImportPeriod(event.target.value);
                    setSelectedImportPeriods([event.target.value]);
                  }}
                >
                  {importPeriods.map((period) => (
                    <option value={period.date} key={period.date}>{period.date}</option>
                  ))}
                </select>
              </label>
              <button type="button" className="download-link" disabled={!excelFile} onClick={handlePreviewExcel}>{t.previewExcel}</button>
              {excelPreview && (
                <div className="import-preview">
                  <strong>{excelPreview.selectedLastDate}</strong>
                  {excelPreview.statementDate && <span>{t.statementDate}: {normalizeAmericanDate(excelPreview.statementDate)}</span>}
                  <span>{excelPreview.partnerCount} partnerů</span>
                  <span>{formatCzk(excelPreview.totalCzk)} / {formatUsd(excelPreview.totalUsd)}</span>
                  <span>Index: {formatIndexValue(excelPreview.performanceLastValue)}</span>
                </div>
              )}
              {excelPreview && (
                <div className="period-picker">
                  <strong>{t.detectedImportPeriods}</strong>
                  <div className="period-checkbox-grid" aria-label={t.detectedImportPeriods}>
                    {(excelPreview.detectedPeriods || excelPreview.availablePeriods || []).map((period) => (
                      <label key={period.date} className="period-checkbox">
                        <input
                          type="radio"
                          name="selected-import-period"
                          checked={selectedImportPeriod === period.date}
                          onChange={() => {
                            setSelectedImportPeriod(period.date);
                            setSelectedImportPeriods([period.date]);
                            setManualImportPeriod('');
                          }}
                        />
                        <span>{period.date}</span>
                        <small>{formatCzk(period.totalCzk || 0)} / {formatUsd(period.totalUsd || 0)}</small>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className="field-label">
                {t.statementDate}
                <input
                  type="text"
                  value={statementDate}
                  onChange={(event) => setStatementDate(event.target.value)}
                  placeholder="18. 5. 2026"
                />
                <span className="field-hint">{t.statementDateHint}</span>
              </label>
              <label className="field-label">
                {t.customImportPeriod}
                <input
                  type="text"
                  value={manualImportPeriod}
                  onChange={(event) => {
                    setManualImportPeriod(event.target.value);
                    setSelectedImportPeriods([]);
                  }}
                  placeholder="9/30/30"
                />
                <span className="field-hint">{t.customImportPeriodHint}</span>
              </label>
              <button type="submit" className="save-btn" disabled={!excelFile || !excelPreview}>{t.confirmImport}</button>
            </form>
            <form className="upload-form" onSubmit={handleApplyInterim}>
              <strong>{t.interimTitle}</strong>
              <p className="tool-help-text">{t.interimHelp}</p>
              {interimInfo?.basis ? (
                <>
                  <div className="import-preview">
                    <span>{t.interimLastStatement}: {interimInfo.basis.anchorDate} · {t.interimRate} {formatRate(interimInfo.basis.exchangeRate)}</span>
                    <span>{t.interimYtdAtStatement} {interimInfo.basis.fundYear}: {formatSignedPercent(interimInfo.basis.grossReturnYtdPct)}</span>
                    {interimInfo.current && (
                      <span>
                        {t.interimActive}: {interimInfo.current.date} · {formatSignedPercent(interimInfo.current.returnPct)} · {t.interimRate} {formatRate(interimInfo.current.exchangeRate)}
                      </span>
                    )}
                  </div>
                  <label className="field-label">
                    {t.interimReturn}
                    <input
                      type="text"
                      inputMode="decimal"
                      value={interimForm.returnPct}
                      onChange={(event) => handleInterimFieldChange('returnPct', event.target.value)}
                      placeholder="+2"
                      required
                    />
                    <span className="field-hint">{t.interimReturnHint}</span>
                  </label>
                  <label className="field-label">
                    {t.interimRate}
                    <input
                      type="text"
                      inputMode="decimal"
                      value={interimForm.exchangeRate}
                      onChange={(event) => handleInterimFieldChange('exchangeRate', event.target.value)}
                      placeholder="21,35"
                      required
                    />
                  </label>
                  <label className="field-label">
                    {t.interimDate}
                    <input
                      type="date"
                      value={interimForm.date}
                      onChange={(event) => handleInterimFieldChange('date', event.target.value)}
                      required
                    />
                    <span className="field-hint">{t.interimDateHint} ({interimInfo.basis.nextStatementDate})</span>
                  </label>
                  <button
                    type="button"
                    className="download-link"
                    disabled={!interimForm.returnPct || !interimForm.exchangeRate}
                    onClick={handlePreviewInterim}
                  >
                    {t.interimPreview}
                  </button>
                  {interimPreview && (
                    <div className="import-preview">
                      <strong>{interimPreview.applied ? t.interimApplied : interimPreview.summary.date}</strong>
                      <span>{t.interimFundTotal}: {formatCzk(interimPreview.summary.totalCzk)} / {formatUsd(interimPreview.summary.totalUsd)}</span>
                      <span>{t.interimNetYtd}: {formatSignedPercent(interimPreview.summary.netReturnYtdPct)}</span>
                      <span>{t.interimPendingFees}: {formatUsd(interimPreview.summary.pendingFeesUsd)} / {formatCzk(interimPreview.summary.pendingFeesCzk)}</span>
                      <div className="mini-log-list">
                        {interimPreview.rows.map((row) => (
                          <div className="mini-log-row" key={row.id}>
                            <strong>{row.name} · {maskValue(row.capitalCzk, formatCzk)}</strong>
                            <span>
                              {maskValue(row.capitalUsd, formatUsd)} ({formatSignedPercent(row.statementCapitalUsd ? ((row.capitalUsd / row.statementCapitalUsd) - 1) * 100 : 0)})
                              {' · '}{formatPercent(row.share)}
                              {row.pendingFeeUsd > 0 && ` · ${t.pendingFee}: ${formatUsd(row.pendingFeeUsd)}`}
                              {row.feeExempt && ` · ${t.interimFeeExempt}`}
                              {row.receivesFees && ` · ${t.interimReceivesFees}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button type="submit" className="save-btn" disabled={!interimPreview || interimPreview.applied}>{t.interimApply}</button>
                  {interimInfo.current && (
                    <button type="button" className="download-link" onClick={handleRevertInterim}>{t.interimRevert}</button>
                  )}
                </>
              ) : (
                <p className="tool-help-text">{t.interimNoBasis}</p>
              )}
            </form>
            <button type="button" className="download-link wide-action" onClick={handleExportCsv}>
              {t.exportCsv}
            </button>
            <button type="button" className="download-link wide-action" onClick={handleDownloadBackup}>
              {t.downloadBackup}
            </button>
            <div className="mini-log-list">
              <strong>{t.importArchive}</strong>
              {importArchive.slice(0, 5).map((item) => (
                <div className="mini-log-row" key={item.id}>
                  <strong>{normalizeAmericanDate(item.statementDate || item.lastDate)} · {formatCzk(item.totalCzk)}</strong>
                  <span>{item.fileName} · {new Date(item.createdAt).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!isAdmin && (
          <div className="tool-panel fund-rules-panel">
            <h2>{t.fundRules}</h2>
            <ul className="fund-rules-list">
              {t.fundRulesItems.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="tool-panel documents-panel">
          <div className="tool-panel-header">
            <h2>{t.shareholderLetters}</h2>
            {isAdmin && (
              <form className="upload-form" onSubmit={handleUploadDocument}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
                />
                <button type="submit" className="save-btn" disabled={!documentFile}>{t.uploadLetter}</button>
              </form>
            )}
          </div>

          {documents.length === 0 ? (
            <p className="empty-chart">{t.noDocuments}</p>
          ) : (
            <div className="document-list">
              {documents.map((document) => (
                <div className="document-row" key={document.id}>
                  <div>
                    <strong>{document.name}</strong>
                    <span>{formatFileSize(document.size)}</span>
                  </div>
                  <div className="document-actions">
                    <button
                      type="button"
                      className="download-link"
                      onClick={() => handleDownloadDocument(document)}
                    >
                      {t.download}
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => handleDeleteDocument(document.id)}
                      >
                        {t.delete}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tool-panel comments-panel">
          <h2>{t.fundUpdates}</h2>
          {isAdmin && (
            <form className="comment-form" onSubmit={handleCreateFundUpdate}>
              <input
                type="text"
                value={fundUpdateTitle}
                onChange={(event) => setFundUpdateTitle(event.target.value)}
                placeholder={t.fundUpdateTitle}
                maxLength="120"
              />
              <textarea
                value={fundUpdateMessage}
                onChange={(event) => setFundUpdateMessage(event.target.value)}
                placeholder={t.fundUpdatePlaceholder}
                maxLength="5000"
                required
              />
              <input
                type="text"
                value={fundUpdateTitleEn}
                onChange={(event) => setFundUpdateTitleEn(event.target.value)}
                placeholder={t.fundUpdateTitleEn}
                maxLength="120"
              />
              <textarea
                value={fundUpdateMessageEn}
                onChange={(event) => setFundUpdateMessageEn(event.target.value)}
                placeholder={t.fundUpdatePlaceholderEn}
                maxLength="5000"
              />
              <button type="submit" className="save-btn">{t.publishUpdate}</button>
            </form>
          )}

          {fundUpdates.length === 0 ? (
            <p className="empty-chart">{t.noFundUpdates}</p>
          ) : (
            <div className="comment-list">
              {fundUpdates.map((update) => (
                <div className="comment-row" key={update.id}>
                  {editingUpdate?.id === update.id ? (
                    <form className="comment-form" onSubmit={handleSaveUpdateEdit}>
                      <input
                        type="text"
                        value={editingUpdate.title}
                        onChange={(event) => handleEditUpdateChange('title', event.target.value)}
                        placeholder={t.fundUpdateTitle}
                        maxLength="120"
                      />
                      <textarea
                        value={editingUpdate.message}
                        onChange={(event) => handleEditUpdateChange('message', event.target.value)}
                        placeholder={t.fundUpdatePlaceholder}
                        maxLength="5000"
                        required
                      />
                      <input
                        type="text"
                        value={editingUpdate.titleEn}
                        onChange={(event) => handleEditUpdateChange('titleEn', event.target.value)}
                        placeholder={t.fundUpdateTitleEn}
                        maxLength="120"
                      />
                      <textarea
                        value={editingUpdate.messageEn}
                        onChange={(event) => handleEditUpdateChange('messageEn', event.target.value)}
                        placeholder={t.fundUpdatePlaceholderEn}
                        maxLength="5000"
                      />
                      <div className="fund-update-actions">
                        <button type="submit" className="save-btn compact-action">{t.save}</button>
                        <button
                          type="button"
                          className="cancel-btn compact-action"
                          onClick={() => setEditingUpdate(null)}
                        >
                          {t.cancel}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="comment-meta">
                        <strong>
                          {(language === 'en' && update.titleEn ? update.titleEn : update.title) || t.fundUpdates}
                        </strong>
                        <div className="fund-update-actions">
                          <span>{new Date(update.createdAt).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}</span>
                          {isAdmin && (
                            <>
                              <button
                                type="button"
                                className="edit-btn compact-action"
                                onClick={() => setEditingUpdate({
                                  id: update.id,
                                  title: update.title || '',
                                  message: update.message || '',
                                  titleEn: update.titleEn || '',
                                  messageEn: update.messageEn || ''
                                })}
                              >
                                {t.editUpdate}
                              </button>
                              <button
                                type="button"
                                className="cancel-btn compact-action"
                                onClick={() => handleDeleteFundUpdate(update.id)}
                              >
                                {t.delete}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <p>{language === 'en' && update.messageEn ? update.messageEn : update.message}</p>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {!isAdmin && (
          <div className="tool-panel emergency-contact-panel">
            <h2>{t.emergencyContact}</h2>
            <p className="tool-help-text">{t.emergencyContactHelp}</p>
            <form className="settings-form" onSubmit={handleSaveEmergencyContact}>
              <input
                type="text"
                value={emergencyContact.name}
                onChange={(event) => handleEmergencyContactChange('name', event.target.value)}
                placeholder={t.emergencyContactName}
              />
              <input
                type="text"
                value={emergencyContact.relationship}
                onChange={(event) => handleEmergencyContactChange('relationship', event.target.value)}
                placeholder={t.emergencyContactRelationship}
              />
              <input
                type="email"
                value={emergencyContact.email}
                onChange={(event) => handleEmergencyContactChange('email', event.target.value)}
                placeholder={t.emergencyContactEmail}
              />
              <input
                type="text"
                value={emergencyContact.phone}
                onChange={(event) => handleEmergencyContactChange('phone', event.target.value)}
                placeholder={t.emergencyContactPhone}
              />
              <textarea
                value={emergencyContact.note}
                onChange={(event) => handleEmergencyContactChange('note', event.target.value)}
                placeholder={t.emergencyContactNote}
                maxLength="2000"
              />
              <button type="submit" className="save-btn">{t.save}</button>
            </form>
            {emergencyContactMessage && <p className="success-text">{emergencyContactMessage}</p>}
          </div>
        )}

        {isAdmin && (
          <>
            <div className="tool-panel log-panel">
              <h2>{t.documentDownloads}</h2>
              {documentDownloadLogs.length === 0 ? (
                <p className="empty-chart">{t.noDocumentDownloads}</p>
              ) : (
                <div className="mini-log-list">
                  {documentDownloadLogs.slice(0, 12).map((log) => (
                    <div className="mini-log-row" key={log.id}>
                      <strong>{log.details?.documentName || '-'}</strong>
                      <span>{log.userName} · {new Date(log.createdAt).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="tool-panel log-panel">
              <h2>{t.auditLog}</h2>
              {auditLogs.length === 0 ? (
                <p className="empty-chart">{t.noAudit}</p>
              ) : (
                <div className="mini-log-list">
                  {auditLogs.slice(0, 12).map((log) => (
                    <div className="mini-log-row" key={log.id}>
                      <strong>{log.action}</strong>
                      <span>{log.userName} · {new Date(log.createdAt).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>
      {!isAdmin && <CompoundInterestCalculator t={t} currency={partnerChartCurrency} />}
      <section className="members-section disclaimer-section">{t.disclaimer}</section>
    </div>
  );
};
